import React, { useState, useEffect, useMemo } from 'react';
import { usePersistentTab } from '../../hooks/usePersistentTab';
import { supabase } from '../../lib/supabaseClient';
import { invokeEdgeFunction } from '../../lib/edgeInvoke';
import { invokeAdminManageUser } from '../../lib/adminManageUserClient';
import { isPartnerScopedRole } from '../../lib/authorization';
import ScheduledDateTimePicker from '../../components/ScheduledDateTimePicker.jsx';
import DarkDatePicker from '../../components/DarkDatePicker.jsx';
import Notification from '../../components/Notification.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import InfoModal from '../../components/InfoModal.jsx';
import DataTablePagination from '../../components/DataTablePagination.jsx';
import CoreBaseTab from './components/CoreBaseTab.jsx';
import { Edit2, Trash2, UserPlus, Shield, X, FileUp, Loader2, Check, Search, FileSpreadsheet, LogIn, KeyRound, Mail, Eye, EyeOff, AlertCircle, CheckCircle, ArrowUpDown, ScrollText, ChevronDown, MoreVertical, Users } from 'lucide-react';
import { exportUsersToExcel } from '../../lib/utils';
import {
  INVITE_START_PLACEHOLDER,
  buildInviteStartAtIso,
  formatSaoPauloDateTimeForInput,
  formatSaoPauloDateTimeLabel,
  getSaoPauloNowDate,
  parseInviteStartInput,
} from '../../lib/saoPauloDateTime.js';

const MultiSelect = ({ options = [], value = [], onChange, placeholder = 'Selecionar...', disabled = false, allLabel = 'Todos' }) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const searchRef = React.useRef(null);
  React.useEffect(() => {
    if (open && searchRef.current) {
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);
  const normalizedValue = (value || []).map(String).filter(Boolean);
  const normalizedSearch = search.trim().toLowerCase();
  const filteredOptions = normalizedSearch
    ? options.filter((o) => String(o.name || '').toLowerCase().includes(normalizedSearch))
    : options;
  const toggle = (id) => {
    if (disabled) return;
    const s = new Set(normalizedValue);
    if (s.has(String(id))) s.delete(String(id));
    else s.add(String(id));
    onChange(Array.from(s));
  };
  const clearOne = (id) => {
    if (disabled) return;
    onChange(value.filter(v => String(v) !== String(id)));
  };
  const clearAll = () => { if (!disabled) onChange([]); };
  const closeDropdown = () => { setOpen(false); setSearch(''); };
  const display = options.filter(o => normalizedValue.includes(String(o.id)));
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setOpen(v => !v)}
        disabled={disabled}
        className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-sm text-white outline-none focus:border-slate-600 focus:ring-1 focus:ring-primary/40 transition min-h-[48px] flex items-center gap-2 text-left hover:border-slate-700 disabled:opacity-70 disabled:cursor-not-allowed"
      >
        <Search size={16} className="text-slate-500 flex-shrink-0" />
        <span className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
          {display.length === 0 ? (
            <span className="text-slate-500">{placeholder}</span>
          ) : (
            display.map(opt => (
              <span key={opt.id} className="inline-flex items-center gap-1 bg-slate-800 border border-slate-700 px-2 py-0.5 rounded text-[11px] text-slate-200">
                <span className="truncate max-w-[140px]">{opt.name}</span>
                <X size={12} className={`text-slate-400 hover:text-white ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`} onClick={(e) => { e.stopPropagation(); clearOne(opt.id); }} />
              </span>
            ))
          )}
        </span>
        {display.length > 0 && (
          <span
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
            className="cursor-pointer text-slate-400 hover:text-white text-sm font-bold px-1 flex-shrink-0"
            title="Limpar seleção"
          >
            ×
          </span>
        )}
        <ChevronDown size={16} className={`text-slate-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && !disabled && (
        <>
          <div
            className="fixed inset-0 z-40 cursor-default"
            onClick={closeDropdown}
            onMouseDown={(e) => { e.preventDefault(); closeDropdown(); }}
            aria-hidden="true"
          />
          <div className="absolute mt-2 w-full max-h-80 overflow-auto bg-slate-950 border border-slate-700 rounded-xl shadow-2xl z-50 ring-1 ring-black/50">
            <div className="sticky top-0 bg-slate-950 border-b border-slate-800 p-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Digite para buscar..."
                  className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-primary/50 transition placeholder:text-slate-600"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={clearAll}
              className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800/80 border-b border-slate-800 font-medium"
            >
              {allLabel}
            </button>
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                Nenhum resultado para "{search}"
              </div>
            ) : (
              filteredOptions.map(opt => (
                <label key={opt.id} className={`flex items-center gap-3 px-4 py-2.5 text-sm text-slate-200 hover:bg-slate-800/60 transition select-none ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    className="accent-orange-500 w-4 h-4 rounded flex-shrink-0"
                    checked={normalizedValue.includes(String(opt.id))}
                    onChange={() => toggle(opt.id)}
                    disabled={disabled}
                  />
                  <span className="truncate">{opt.name}</span>
                </label>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

const getUserLogActionDetails = (action, meta = {}) => {
  const normalized = String(action || '').trim();
  const labels = {
    'user.created': 'Usuário criado',
    'user.updated': 'Usuário atualizado',
    'user.deleted': 'Usuário excluído',
    'user.invite_resent': 'Convite reenviado',
    'user.password_reset_sent': 'Link de redefinição enviado',
    'user.password_set_by_admin': 'Senha definida por admin',
  };
  const fieldLabels = {
    full_name: 'Nome',
    email: 'E-mail',
    cpf: 'CPF',
    whatsapp: 'WhatsApp',
    cargo: 'Cargo',
    area: 'Área',
    company_id: 'Empresa',
    linked_company_ids: 'Empresas vinculadas',
    sas_partner_id: 'Parceiro SAS',
    role: 'Perfil',
    status: 'Status',
  };
  const changedFields = Array.isArray(meta?.changed_fields)
    ? meta.changed_fields
      .map((field) => fieldLabels[String(field || '').trim()] || null)
      .filter(Boolean)
    : [];
  const baseLabel = labels[normalized] || normalized || 'Ação registrada';
  return {
    baseLabel,
    changedFields: normalized === 'user.updated' ? changedFields : [],
  };
};

const SUMMARY_STATUS_ORDER = ['Ativo', 'Programado', 'Pendente', 'Inativo'];

const SUMMARY_CARD_ORDER = [
  { key: 'totalUsers', label: 'Usuários', accentClass: 'text-orange-400', testId: 'users-summary-total-users' },
  { key: 'status:Ativo', label: 'Ativos', accentClass: 'text-teal-400' },
  { key: 'status:Programado', label: 'Programados', accentClass: 'text-amber-300' },
  { key: 'status:Pendente', label: 'Pendentes', accentClass: 'text-amber-300' },
  { key: 'status:Inativo', label: 'Inativos', accentClass: 'text-rose-400' },
  { key: 'role:Admin', label: 'Admin', accentClass: 'text-violet-300' },
  { key: 'role:Gestor Parceiro', label: 'Gestor Parceiro', accentClass: 'text-violet-300' },
  { key: 'role:Parceiro SAS', label: 'Parceiro SAS', accentClass: 'text-violet-300' },
  { key: 'role:Ponto Focal', label: 'Ponto Focal', accentClass: 'text-violet-300' },
  { key: 'role:Aluno', label: 'Aluno', accentClass: 'text-violet-300' },
];

const INVITE_START_MODES = {
  immediate: 'immediate',
  scheduled: 'scheduled',
};

const AWAITING_INVITE_STATUSES = new Set(['Pendente', 'Programado']);

const normalizeLinkedCompanyIds = (companyId, companyIds = []) => {
  const ids = new Set();
  if (companyId) ids.add(String(companyId));
  (Array.isArray(companyIds) ? companyIds : []).forEach((value) => {
    if (!value) return;
    ids.add(String(value));
  });
  return Array.from(ids);
};

const normalizeLinkedCompanies = (linkedCompanies = [], companyId = null, companyName = '') => {
  const map = new Map();
  (Array.isArray(linkedCompanies) ? linkedCompanies : []).forEach((item) => {
    const id = String(item?.id || item?.company_id || '').trim();
    if (!id) return;
    map.set(id, {
      id,
      name: item?.name || item?.company_name || '-',
    });
  });
  if (companyId) {
    const normalizedId = String(companyId);
    if (!map.has(normalizedId)) {
      map.set(normalizedId, { id: normalizedId, name: companyName || '-' });
    }
  }
  return Array.from(map.values());
};

const buildCompanyLabel = (linkedCompanies = [], fallback = '-') => {
  const names = normalizeLinkedCompanies(linkedCompanies)
    .map((item) => item.name)
    .filter(Boolean);
  return names.length > 0 ? names.join(', ') : (fallback || '-');
};

const getInviteDispatchFeedback = (dispatch, fallback = 'failed') => {
  const reason = dispatch?.reason ? String(dispatch.reason) : '';
  const detailCode = dispatch?.data?.error ? String(dispatch.data.error) : '';
  const detailMessage = dispatch?.data?.message ? String(dispatch.data.message) : '';
  const detailLabel = [detailCode, detailMessage].filter(Boolean).join(': ');
  return detailLabel || reason || fallback;
};

export const SummaryCard = ({ label, value, accentClass = 'text-orange-400', testId }) => (
  <div
    data-testid={testId}
    className="w-full rounded-lg border border-slate-800 bg-slate-900/80 px-3 py-2.5 shadow-md shadow-slate-950/10"
  >
    <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">{label}</div>
    <div className={`mt-1 text-2xl font-black leading-none ${accentClass}`}>{value}</div>
  </div>
);

const UserActionsDropdown = ({
  user,
  disabled,
  canUseAdminOnlyActions,
  canScopedUserResendInvite,
  auth,
  passwordResetTargetId,
  setPasswordTargetId,
  impersonatingUserId,
  onEdit,
  onResendInvite,
  onSendPasswordReset,
  onSetPassword,
  onImpersonate,
  onDelete,
}) => {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const disabledActions = Boolean(disabled);

  const close = () => setOpen(false);
  const trigger = () => setOpen((v) => !v);

  const actionItems = [];

  actionItems.push({
    key: 'edit',
    label: 'Editar',
    icon: Edit2,
    iconClass: 'text-slate-300',
    onClick: () => onEdit?.(user),
    disabled: disabledActions,
  });

  const resendAllowed = canScopedUserResendInvite?.(user);
  actionItems.push({
    key: 'resend_invite',
    label: 'Reenviar Convite',
    icon: Shield,
    iconClass: 'text-teal-400',
    onClick: () => onResendInvite?.(user),
    disabled: disabledActions || !resendAllowed,
    hint: !resendAllowed ? 'Não disponível para este usuário' : null,
  });

  if (canUseAdminOnlyActions) {
    actionItems.push({
      key: 'password_reset',
      label: 'Enviar Link de Alteração de Senha',
      icon: Mail,
      iconClass: 'text-amber-300',
      onClick: () => onSendPasswordReset?.(user),
      disabled: disabledActions || passwordResetTargetId === (user?.id || user?.email),
      loading: passwordResetTargetId === (user?.id || user?.email),
    });
    actionItems.push({
      key: 'set_password',
      label: 'Definir Nova Senha (Admin)',
      icon: KeyRound,
      iconClass: 'text-orange-400',
      onClick: () => onSetPassword?.(user),
      disabled: disabledActions || setPasswordTargetId === user?.id,
      loading: setPasswordTargetId === user?.id,
    });
    if (auth?.isAdmin) {
      const isAdminTarget = String(user?.profile || '').toLowerCase() === 'admin';
      const isSelf = auth?.session?.user?.id === user?.id;
      actionItems.push({
        key: 'impersonate',
        label: 'Impersonar',
        icon: LogIn,
        iconClass: 'text-amber-300',
        onClick: () => onImpersonate?.(user),
        disabled: disabledActions || isAdminTarget || isSelf || impersonatingUserId === user?.id,
        loading: impersonatingUserId === user?.id,
        hint: isAdminTarget ? 'Não é possível impersonar outro Admin' : isSelf ? 'Não é possível impersonar sua própria conta' : null,
      });
    }
    const canDelete = auth?.session?.user?.id !== user?.id;
    actionItems.push({
      key: 'delete',
      label: 'Excluir',
      icon: Trash2,
      iconClass: 'text-rose-500',
      onClick: () => onDelete?.(user?.id),
      disabled: disabledActions || !canDelete,
      destructive: true,
      hint: !canDelete ? 'Não é possível excluir sua própria conta' : null,
    });
  }

  return (
    <div className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); trigger(); }}
        disabled={disabledActions}
        title="Ações"
        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/80 border border-transparent hover:border-slate-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <MoreVertical size={18} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 cursor-default"
            onClick={(e) => { e.stopPropagation(); close(); }}
            onMouseDown={(e) => { e.preventDefault(); close(); }}
            aria-hidden="true"
          />
          <div
            ref={menuRef}
            className={`fixed min-w-[240px] max-w-[280px] w-[min(280px,calc(100vw-16px))] overflow-hidden rounded-xl border border-slate-700 bg-slate-950 shadow-2xl shadow-black/60 z-[9999] ring-1 ring-black/60 py-1.5 animate-fade-in`}
            style={(function computeMenuStyle() {
              const triggerEl = triggerRef.current;
              const menuEl = menuRef.current;
              if (!triggerEl) return {};
              const rect = triggerEl.getBoundingClientRect();
              const fallbackWidth = 280;
              const menuWidth = menuEl ? Math.max(menuEl.offsetWidth, fallbackWidth) : fallbackWidth;
              const margin = 6;
              const vw = window.innerWidth;
              const vh = window.innerHeight;
              const neededRight = rect.right + menuWidth + margin;
              const neededBottom = rect.bottom + 360 + margin;
              const flipH = neededRight > vw && (rect.left - menuWidth - margin) >= 0;
              const flipV = neededBottom > vh && (rect.top - 360 - margin) >= 0;
              const style = {};
              if (flipV) style.bottom = `${Math.max(8, vh - rect.top + margin)}px`;
              else style.top = `${rect.bottom + margin}px`;
              if (flipH) style.right = `${Math.max(8, vw - rect.right)}px`;
              else {
                let left = rect.right - menuWidth;
                if (left < 8) left = 8;
                const maxLeft = vw - menuWidth - 8;
                if (left > maxLeft) left = Math.max(8, maxLeft);
                style.left = `${left}px`;
              }
              return style;
            })()}
          >
            <div className="px-3 py-2 border-b border-slate-800/70 mb-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Ações do Usuário</div>
              <div className="mt-0.5 text-xs font-semibold text-white truncate">{user?.name || user?.email || 'Usuário'}</div>
              <div className="text-[10px] text-slate-500 truncate">{user?.email || ''}</div>
            </div>
            {actionItems.map((item) => {
              const Icon = item.icon;
              const isDestructive = Boolean(item.destructive);
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.disabled) return;
                    close();
                    item.onClick?.();
                  }}
                  disabled={item.disabled}
                  className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition ${
                    item.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : isDestructive
                        ? 'text-slate-200 hover:bg-rose-500/10 hover:text-rose-200'
                        : 'text-slate-200 hover:bg-slate-800/70 hover:text-white'
                  }`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    {item.loading ? (
                      <Loader2 size={16} className="animate-spin text-slate-300" />
                    ) : (
                      <Icon size={16} className={item.iconClass} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold leading-tight">
                      {item.label}
                    </div>
                    {item.hint && (
                      <div className="mt-0.5 text-[10px] text-slate-500 leading-snug">
                        {item.hint}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const BulkActionsDropdown = ({
  selectedCount = 0,
  canUseAdminOnlyActions = false,
  onResendInvite,
  onSendPasswordReset,
  onDelete,
}) => {
  const [open, setOpen] = React.useState(false);
  const close = () => setOpen(false);
  const toggle = () => setOpen((v) => !v);
  const disabled = selectedCount < 1;
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);

  return (
    <div className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        disabled={disabled}
        title="Ações em massa"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-xs font-bold shadow-md shadow-orange-950/30 border border-orange-400/50 transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Users size={14} />
        Ações em massa
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 cursor-default"
            onClick={(e) => { e.stopPropagation(); close(); }}
            onMouseDown={(e) => { e.preventDefault(); close(); }}
            aria-hidden="true"
          />
          <div
            ref={menuRef}
            className={`fixed min-w-[240px] max-w-[280px] w-[min(280px,calc(100vw-16px))] overflow-hidden rounded-xl border border-slate-700 bg-slate-950 shadow-2xl shadow-black/60 z-[9999] ring-1 ring-black/60 py-1.5 animate-fade-in`}
            style={(function computeMenuStyle() {
              const triggerEl = triggerRef.current;
              const menuEl = menuRef.current;
              if (!triggerEl) return {};
              const rect = triggerEl.getBoundingClientRect();
              const fallbackWidth = 280;
              const menuWidth = menuEl ? Math.max(menuEl.offsetWidth, fallbackWidth) : fallbackWidth;
              const margin = 6;
              const vw = window.innerWidth;
              const vh = window.innerHeight;
              const neededRight = rect.right + menuWidth + margin;
              const neededBottom = rect.bottom + 320 + margin;
              const flipH = neededRight > vw && (rect.left - menuWidth - margin) >= 0;
              const flipV = neededBottom > vh && (rect.top - 320 - margin) >= 0;
              const style = {};
              if (flipV) style.bottom = `${Math.max(8, vh - rect.top + margin)}px`;
              else style.top = `${rect.bottom + margin}px`;
              if (flipH) style.right = `${Math.max(8, vw - rect.right)}px`;
              else {
                let left = rect.right - menuWidth;
                if (left < 8) left = 8;
                const maxLeft = vw - menuWidth - 8;
                if (left > maxLeft) left = Math.max(8, maxLeft);
                style.left = `${left}px`;
              }
              return style;
            })()}
          >
            <div className="px-3 py-2 border-b border-slate-800/70 mb-1">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Ações em massa</div>
              <div className="mt-0.5 text-xs font-semibold text-white">
                {selectedCount} {selectedCount === 1 ? 'usuário selecionado' : 'usuários selecionados'}
              </div>
              <div className="mt-1 text-[10px] text-slate-500 leading-snug">
                Editar e Impersonar não estão disponíveis em lote.
              </div>
            </div>
            {[
              {
                key: 'resend_invite',
                label: 'Reenviar Convite',
                icon: Shield,
                iconClass: 'text-teal-400',
                onClick: onResendInvite,
                hint: 'Exibe opções de canais antes de enviar',
              },
              ...(canUseAdminOnlyActions ? [
                {
                  key: 'password_reset',
                  label: 'Enviar Link de Alteração de Senha',
                  icon: Mail,
                  iconClass: 'text-amber-300',
                  onClick: onSendPasswordReset,
                  hint: 'Envia um e-mail de recuperação para cada usuário selecionado',
                },
                {
                  key: 'delete',
                  label: 'Excluir',
                  icon: Trash2,
                  iconClass: 'text-rose-500',
                  onClick: onDelete,
                  destructive: true,
                  hint: 'Não é possível excluir sua própria conta',
                },
              ] : []),
            ].map((item) => {
              const Icon = item.icon;
              const isDestructive = Boolean(item.destructive);
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    close();
                    item.onClick?.();
                  }}
                  className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition ${
                    isDestructive
                      ? 'text-slate-200 hover:bg-rose-500/10 hover:text-rose-200'
                      : 'text-slate-200 hover:bg-slate-800/70 hover:text-white'
                  }`}
                >
                  <div className="mt-0.5 flex-shrink-0">
                    <Icon size={16} className={item.iconClass} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold leading-tight">
                      {item.label}
                    </div>
                    {item.hint && (
                      <div className="mt-0.5 text-[10px] text-slate-500 leading-snug">
                        {item.hint}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

import BulkCreateUserModal from './components/BulkCreateUserModal';

const AdminUsers = ({ partner, companies, users, setUsers, partners, role, userCompanyId, readOnly = false, auth }) => {
  const effectiveRole = role || auth?.profileRole || auth?.profile?.role || '';
  const effectiveUserCompanyId = userCompanyId || auth?.profile?.companyId || auth?.profile?.company_id || null;
  const effectivePartnerId = partner?.id || auth?.profile?.sasPartnerId || '';
  const isScopedUserManager = effectiveRole === 'Ponto Focal' || effectiveRole === 'Parceiro SAS';
  const isEmailOnlyInviteRole = isScopedUserManager;
  const canViewUserLogs = effectiveRole === 'Admin' || effectiveRole === 'Parceiro SAS' || effectiveRole === 'Ponto Focal';
  const canCreateUsers = !readOnly && !isScopedUserManager;
  const canManageRoleDefinitions = !readOnly && !isScopedUserManager;
  const canManageScopedUsers = !readOnly;
  const canUseAdminOnlyActions = !readOnly && !isScopedUserManager;
  const canScopedUserResendInvite = (user) => !isScopedUserManager || String(user?.status || '').trim() !== 'Ativo';
  const BULK_RESEND_PERIOD_OPTIONS = [
    { value: '7d', label: 'Últimos 7 dias' },
    { value: '30d', label: 'Últimos 30 dias' },
    { value: '1y', label: 'Último Ano' },
    { value: 'all', label: 'Reenvio para Todos' },
  ];
  const [showManual, setShowManual] = useState(false);
  const [externalPrefill, setExternalPrefill] = useState(false);
  const [showMass, setShowMass] = useState(false);
  const [importData, setImportData] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [manualForm, setManualForm] = useState({ 
    name: '', 
    email: '',
    password: '',
    confirmPassword: '',
    companyId: '', 
    companyIds: [],
    cpf: '',
    whatsapp: '', 
    profile: 'Aluno', 
    partnerId: '', 
    status: 'Pendente',
    cargo: '',
    area: '',
    inviteStartMode: '',
    inviteStartDate: '',
  });
  const [companyCargos, setCompanyCargos] = useState([]);
  const [companyAreas, setCompanyAreas] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [infoModal, setInfoModal] = useState({ isOpen: false, title: '', content: '' });
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);
  const [activeTab, setActiveTab] = usePersistentTab('admin_users', 'geral');
  const [userLogs, setUserLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsSearchInput, setLogsSearchInput] = useState('');
  const [logsSearch, setLogsSearch] = useState('');
  const [rolesMatrix, setRolesMatrix] = useState(null);
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showManageRolesModal, setShowManageRolesModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleNameInput, setRoleNameInput] = useState('');
  const [resendInviteModal, setResendInviteModal] = useState({
    isOpen: false,
    user: null,
    channels: {
      email: true,
      sms: true,
      whatsapp: true,
    },
  });
  const [resendSubmitting, setResendSubmitting] = useState(false);
  const [passwordResetTargetId, setPasswordResetTargetId] = useState(null);
  const [bulkResendModal, setBulkResendModal] = useState({
    isOpen: false,
    channels: {
      email: true,
      sms: false,
      whatsapp: false,
    },
    statuses: {
      pending: false,
      active: true,
    },
    startDate: '',
    endDate: '',
  });
  const [bulkResendSubmitting, setBulkResendSubmitting] = useState(false);
  const [selectedResendModal, setSelectedResendModal] = useState({
    isOpen: false,
    channels: {
      email: true,
      sms: false,
      whatsapp: false,
    },
  });
  const [selectedResendSubmitting, setSelectedResendSubmitting] = useState(false);
  const [bulkPasswordResetModal, setBulkPasswordResetModal] = useState({
    isOpen: false,
  });
  const [bulkPasswordResetSubmitting, setBulkPasswordResetSubmitting] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState({
    isOpen: false,
  });
  const [bulkDeleteSubmitting, setBulkDeleteSubmitting] = useState(false);
  const [setPasswordModal, setSetPasswordModal] = useState({
    isOpen: false,
    user: null,
    password: '',
    confirmPassword: '',
  });
  const [setPasswordSubmitting, setSetPasswordSubmitting] = useState(false);
  const [setPasswordTargetId, setSetPasswordTargetId] = useState(null);
  const [setPasswordShow, setSetPasswordShow] = useState(false);
  const [setPasswordConfirmShow, setSetPasswordConfirmShow] = useState(false);
  const [manualPasswordShow, setManualPasswordShow] = useState(false);
  const [manualPasswordConfirmShow, setManualPasswordConfirmShow] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState([]);
  const [partnerFilter, setPartnerFilter] = useState([]);
  const [sortConfig, setSortConfig] = useState({ field: 'activation', direction: 'desc' });
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const [manualDraftHydrated, setManualDraftHydrated] = useState(false);
  const USERS_FILTERS_STORAGE_KEY = 'sofera:admin_users:filters:v1';
  const MANUAL_MODAL_DRAFT_STORAGE_KEY = 'sofera:admin_users:manual_modal_draft:v1';
  const MANUAL_PASSWORD_CACHE_STORAGE_KEY = 'sofera:admin_users:manual_password_cache:v1';

  const clearManualModalDraftStorage = () => {
    try {
      window.localStorage.removeItem(MANUAL_MODAL_DRAFT_STORAGE_KEY);
    } catch {}
  };

  const getPersistedAdminPassword = (userId) => {
    if (!userId) return null;
    try {
      const raw = window.localStorage.getItem(MANUAL_PASSWORD_CACHE_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const cached = parsed?.[String(userId)];
      return cached && typeof cached === 'object' ? cached : null;
    } catch {
      return null;
    }
  };

  const persistAdminPassword = (userId, password, confirmPassword) => {
    if (!userId || !password || !confirmPassword) return;
    try {
      const raw = window.localStorage.getItem(MANUAL_PASSWORD_CACHE_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      parsed[String(userId)] = {
        password: String(password),
        confirmPassword: String(confirmPassword),
        updatedAt: new Date().toISOString(),
      };
      window.localStorage.setItem(MANUAL_PASSWORD_CACHE_STORAGE_KEY, JSON.stringify(parsed));
    } catch {}
  };

  // Prefill "Novo Usuário" a partir da OS
  useEffect(() => {
    const KEY = 'prefill_user_from_os';
    const prefill = (d = {}) => {
      const companyId = d.companyId || (companies || []).find(c => String(c.name || '').toLowerCase() === String(d.companyName || '').toLowerCase())?.id || '';
      const partnerId = d.partnerId || (partners || []).find(p => String(p.name || '').toLowerCase() === String(d.partnerName || '').toLowerCase())?.id || '';
      setManualForm(m => ({
        ...m,
        name: d.name || '',
        email: d.email || '',
        whatsapp: d.whatsapp || '',
        companyId,
        companyIds: companyId ? [companyId] : [],
        partnerId,
        profile: 'Ponto Focal',
        status: 'Pendente',
        cpf: '',
        inviteStartMode: '',
        inviteStartDate: '',
      }));
      setExternalPrefill(true);
      setShowManual(true);
    };
    const handler = (e) => prefill(e.detail || {});
    window.addEventListener('open-user-create-from-os', handler);
    try {
      const cached = sessionStorage.getItem(KEY);
      if (cached) {
        sessionStorage.removeItem(KEY);
        prefill(JSON.parse(cached));
      }
    } catch {}
    return () => window.removeEventListener('open-user-create-from-os', handler);
  }, [companies, partners]);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(MANUAL_MODAL_DRAFT_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.showManual) {
          setShowManual(true);
          setEditingId(parsed?.editingId || null);
          setExternalPrefill(Boolean(parsed?.externalPrefill));
          setManualForm((prev) => ({
            ...prev,
            ...(parsed?.manualForm && typeof parsed.manualForm === 'object' ? parsed.manualForm : {}),
          }));
          if (typeof parsed?.autoEnroll === 'boolean') setAutoEnroll(parsed.autoEnroll);
          if (Array.isArray(parsed?.selectedCourses)) setSelectedCourses(parsed.selectedCourses);
          if (typeof parsed?.selectedClass === 'string') setSelectedClass(parsed.selectedClass);
          if (parsed?.validityDays != null && !Number.isNaN(Number(parsed.validityDays))) {
            setValidityDays(Number(parsed.validityDays));
          }
        }
      }
    } catch {}
    finally {
      setManualDraftHydrated(true);
    }
  }, []);

  const BASE_ROLES = ['Aluno','Instrutor','Coordenador','Ponto Focal','Parceiro SAS','Gestor Parceiro','Admin'];
  const PERMISSION_DEFS = [
    { key: 'access_courses', label: 'Acessar cursos' },
    { key: 'watch_lessons', label: 'Assistir aulas' },
    { key: 'take_exams', label: 'Fazer provas' },
    { key: 'download_materials', label: 'Baixar materiais' },
    { key: 'view_own_progress', label: 'Ver próprio progresso' },
    { key: 'view_own_certificates', label: 'Ver certificados próprios' },
    { key: 'view_company_certificates', label: 'Ver certificados da empresa' },
    { key: 'download_company_certificates', label: 'Baixar certificados da empresa' },
    { key: 'create_courses', label: 'Criar cursos' },
    { key: 'edit_courses', label: 'Editar cursos' },
    { key: 'create_lessons', label: 'Criar aulas' },
    { key: 'create_exams', label: 'Criar provas' },
    { key: 'view_course_students', label: 'Ver alunos do curso' },
    { key: 'view_students_progress', label: 'Ver progresso dos alunos' },
    { key: 'enroll_students', label: 'Matricular alunos' },
    { key: 'create_classes', label: 'Criar turmas' },
    { key: 'create_users', label: 'Criar usuários' },
    { key: 'edit_users', label: 'Editar usuários' },
    { key: 'deactivate_users', label: 'Desativar usuários' },
    { key: 'view_linked_companies', label: 'Ver empresas vinculadas' },
    { key: 'create_company', label: 'Criar empresa' },
    { key: 'edit_company', label: 'Editar empresa' },
    { key: 'view_linked_companies_reports', label: 'Ver relatórios das empresas vinculadas' },
    { key: 'export_reports', label: 'Exportar relatórios' },
    { key: 'open_os', label: 'Abrir OS' },
    { key: 'view_own_os', label: 'Ver suas próprias OS' },
    { key: 'edit_os', label: 'Editar OS' },
    { key: 'comment_os', label: 'Comentar em OS' },
    { key: 'attach_os', label: 'Inserir anexos em OS' },
    { key: 'close_os', label: 'Encerrar OS' },
    { key: 'create_roles', label: 'Criar perfis (roles)' },
    { key: 'edit_roles', label: 'Editar perfis' },
    { key: 'define_role_permissions', label: 'Definir permissões de perfis' },
    { key: 'delete_roles', label: 'Excluir perfis' },
    { key: 'configure_system', label: 'Configurar sistema' },
  ];

  const DEFAULT_MATRIX = {
    'Aluno': {
      access_courses: true,
      watch_lessons: true,
      take_exams: true,
      download_materials: true,
      view_own_progress: true,
      view_own_certificates: true,
      view_company_certificates: false,
      download_company_certificates: false,
      create_courses: false,
      edit_courses: false,
      create_lessons: false,
      create_exams: false,
      view_course_students: false,
      view_students_progress: false,
      enroll_students: false,
      create_classes: false,
      create_users: false,
      edit_users: false,
      deactivate_users: false,
      view_linked_companies: false,
      create_company: false,
      edit_company: false,
      view_linked_companies_reports: false,
      export_reports: false,
      open_os: false,
      view_own_os: false,
      edit_os: false,
      comment_os: false,
      attach_os: false,
      close_os: false,
      create_roles: false,
      edit_roles: false,
      define_role_permissions: false,
      delete_roles: false,
      configure_system: false,
    },
    'Instrutor': {
      access_courses: true,
      watch_lessons: true,
      take_exams: false,
      download_materials: true,
      view_own_progress: true,
      view_own_certificates: true,
      view_company_certificates: false,
      download_company_certificates: false,
      create_courses: true,
      edit_courses: true,
      create_lessons: true,
      create_exams: true,
      view_course_students: true,
      view_students_progress: true,
      enroll_students: false,
      create_classes: false,
      create_users: false,
      edit_users: false,
      deactivate_users: false,
      view_linked_companies: false,
      create_company: false,
      edit_company: false,
      view_linked_companies_reports: false,
      export_reports: false,
      open_os: false,
      view_own_os: false,
      edit_os: false,
      comment_os: false,
      attach_os: false,
      close_os: false,
      create_roles: false,
      edit_roles: false,
      define_role_permissions: false,
      delete_roles: false,
      configure_system: false,
    },
    'Coordenador': {
      access_courses: true,
      watch_lessons: true,
      take_exams: false,
      download_materials: true,
      view_own_progress: true,
      view_own_certificates: true,
      view_company_certificates: true,
      download_company_certificates: true,
      create_courses: false,
      edit_courses: false,
      create_lessons: false,
      create_exams: false,
      view_course_students: true,
      view_students_progress: true,
      enroll_students: true,
      create_classes: true,
      create_users: true,
      edit_users: true,
      deactivate_users: true,
      view_linked_companies: false,
      create_company: false,
      edit_company: false,
      view_linked_companies_reports: true,
      export_reports: true,
      open_os: false,
      view_own_os: false,
      edit_os: false,
      comment_os: false,
      attach_os: false,
      close_os: false,
      create_roles: false,
      edit_roles: false,
      define_role_permissions: false,
      delete_roles: false,
      configure_system: false,
    },
    'Ponto Focal': {
      access_courses: true,
      watch_lessons: true,
      take_exams: true,
      download_materials: true,
      view_own_progress: true,
      view_own_certificates: true,
      view_company_certificates: true,
      download_company_certificates: true,
      create_courses: false,
      edit_courses: false,
      create_lessons: false,
      create_exams: false,
      view_course_students: false,
      view_students_progress: true,
      enroll_students: false,
      create_classes: false,
      create_users: false,
      edit_users: false,
      deactivate_users: false,
      view_linked_companies: false,
      create_company: false,
      edit_company: false,
      view_linked_companies_reports: true,
      export_reports: true,
      open_os: false,
      view_own_os: false,
      edit_os: false,
      comment_os: false,
      attach_os: false,
      close_os: false,
      create_roles: false,
      edit_roles: false,
      define_role_permissions: false,
      delete_roles: false,
      configure_system: false,
    },
    'Parceiro SAS': {
      access_courses: false,
      watch_lessons: false,
      take_exams: false,
      download_materials: false,
      view_own_progress: true,
      view_own_certificates: false,
      view_company_certificates: true,
      download_company_certificates: true,
      create_courses: false,
      edit_courses: false,
      create_lessons: false,
      create_exams: false,
      view_course_students: false,
      view_students_progress: false,
      enroll_students: false,
      create_classes: false,
      create_users: false,
      edit_users: false,
      deactivate_users: false,
      view_linked_companies: true,
      create_company: false,
      edit_company: false,
      view_linked_companies_reports: true,
      export_reports: false,
      open_os: true,
      view_own_os: true,
      edit_os: false,
      comment_os: true,
      attach_os: true,
      close_os: false,
      create_roles: false,
      edit_roles: false,
      define_role_permissions: false,
      delete_roles: false,
      configure_system: false,
    },
    'Gestor Parceiro': {
      access_courses: false,
      watch_lessons: false,
      take_exams: false,
      download_materials: false,
      view_own_progress: true,
      view_own_certificates: false,
      view_company_certificates: true,
      download_company_certificates: true,
      create_courses: false,
      edit_courses: false,
      create_lessons: false,
      create_exams: false,
      view_course_students: false,
      view_students_progress: false,
      enroll_students: false,
      create_classes: false,
      create_users: false,
      edit_users: false,
      deactivate_users: false,
      view_linked_companies: true,
      create_company: false,
      edit_company: false,
      view_linked_companies_reports: true,
      export_reports: false,
      open_os: true,
      view_own_os: true,
      edit_os: false,
      comment_os: true,
      attach_os: true,
      close_os: false,
      create_roles: false,
      edit_roles: false,
      define_role_permissions: false,
      delete_roles: false,
      configure_system: false,
    },
    'Admin': {
      access_courses: true,
      watch_lessons: true,
      take_exams: true,
      download_materials: true,
      view_own_progress: true,
      view_own_certificates: true,
      view_company_certificates: true,
      download_company_certificates: true,
      create_courses: true,
      edit_courses: true,
      create_lessons: true,
      create_exams: true,
      view_course_students: true,
      view_students_progress: true,
      enroll_students: true,
      create_classes: true,
      create_users: true,
      edit_users: true,
      deactivate_users: true,
      view_linked_companies: true,
      create_company: true,
      edit_company: true,
      view_linked_companies_reports: true,
      export_reports: true,
      open_os: true,
      view_own_os: true,
      edit_os: true,
      comment_os: true,
      attach_os: true,
      close_os: true,
      create_roles: true,
      edit_roles: true,
      define_role_permissions: true,
      delete_roles: true,
      configure_system: true,
    },
  };

  const rolesList = useMemo(() => {
    const keys = rolesMatrix ? Object.keys(rolesMatrix) : BASE_ROLES;
    return Array.from(new Set([...keys])).sort((a,b)=>a.localeCompare(b,'pt-BR'))
  }, [rolesMatrix]);

  useEffect(() => {
    // Load roles on mount so both tabs have access
    const load = async () => {
      try {
        const { data, error } = await supabase.from('role_permissions').select('role, permissions');
        if (error) throw error;
        const mapping = {};
        for (const r of BASE_ROLES) mapping[r] = { ...(DEFAULT_MATRIX[r] || {}) };
        for (const row of data || []) {
          const role = row.role;
          const perms = row.permissions || {};
          mapping[role] = { ...(mapping[role] || {}), ...perms };
        }
        setRolesMatrix(mapping);
      } catch (e) {
        // fallback to defaults when table missing or RLS denies
        const mapping = {};
        for (const r of BASE_ROLES) mapping[r] = { ...(DEFAULT_MATRIX[r] || {}) };
        setRolesMatrix(mapping);
      }
    };
    load();
  }, []);

  const toggleRolePerm = (role, key) => {
    setRolesMatrix((prev) => ({
      ...prev,
      [role]: { ...(prev?.[role] || {}), [key]: !(prev?.[role]?.[key]) }
    }));
  };

  const saveMatrix = async () => {
    if (!rolesMatrix) return;
    setSavingMatrix(true);
    try {
      const rows = rolesList.map((r) => ({ role: r, permissions: rolesMatrix[r] || {} }));
      const { error } = await supabase.rpc('admin_upsert_role_permissions', { p_rows: rows });
      if (error) throw error;
      setNotification({ message: 'Permissões salvas com sucesso.', type: 'success' });
    } catch (e) {
      setNotification({ message: 'Falha ao salvar permissões. ' + (e?.message || ''), type: 'error' });
    } finally {
      setSavingMatrix(false);
    }
  };

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleNameInput('');
    setShowRoleModal(true);
  };

  const openEditRole = (name) => {
    setEditingRole(name);
    setRoleNameInput(name);
    setShowRoleModal(true);
  };

  const persistRole = async () => {
    const name = (roleNameInput || '').trim();
    if (!name) return;
    const perms = rolesMatrix?.[editingRole || name] || {};
    const nextPerms = Object.keys(perms).length ? perms : Object.fromEntries(PERMISSION_DEFS.map(p=>[p.key,false]));
    try {
      if (editingRole && editingRole !== name) {
        // create new and delete old via RPCs (bypass RLS safely with admin check)
        const { error: upErr } = await supabase.rpc('admin_upsert_role_permissions', { p_rows: [{ role: name, permissions: nextPerms }] });
        if (upErr) throw upErr;
        const { error: delErr } = await supabase.rpc('admin_delete_role_permission', { p_role: editingRole });
        if (delErr) throw delErr;
        setRolesMatrix((prev)=>{
          const copy = { ...(prev||{}) };
          copy[name] = copy[editingRole] || nextPerms;
          delete copy[editingRole];
          return copy;
        });
      } else {
        const { error } = await supabase.rpc('admin_upsert_role_permissions', { p_rows: [{ role: name, permissions: nextPerms }] });
        if (error) throw error;
        setRolesMatrix((prev)=>({ ...(prev||{}), [name]: nextPerms }));
      }
      setShowRoleModal(false);
    } catch (e) {
      setNotification({ message: 'Erro ao salvar perfil: ' + (e?.message||''), type: 'error' });
    }
  };

  const deleteRole = async (name) => {
    try {
      const { error } = await supabase.rpc('admin_delete_role_permission', { p_role: name });
      if (error) throw error;
      setRolesMatrix((prev)=>{
        const copy = { ...(prev||{}) };
        delete copy[name];
        return copy;
      });
      setNotification({ message: 'Perfil excluído com sucesso.', type: 'success' });
    } catch (e) {
      setNotification({ message: 'Erro ao excluir perfil: ' + (e?.message||''), type: 'error' });
    }
  };

  const confirmDeleteRole = (role) => {
    let extraWarning = '';
    if (role === 'Admin') {
      extraWarning = ' ATENÇÃO: Excluir o perfil "Admin" pode bloquear o acesso administrativo ao sistema se houver dependências diretas. Prossiga apenas se tiver certeza.';
    }
    setConfirmModal({
      isOpen: true,
      title: 'Excluir Perfil',
      message: `Tem certeza que deseja excluir o perfil "${role}"? Essa ação não pode ser desfeita.${extraWarning}`,
      onConfirm: async () => {
        await deleteRole(role);
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      }
    });
  };

  // Auto-enrollment state
  const [autoEnroll, setAutoEnroll] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [showOnlySelectedCourses, setShowOnlySelectedCourses] = useState(false);
  const [selectedClass, setSelectedClass] = useState('');
  const [validityDays, setValidityDays] = useState(365);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);

  const toggleCourseSelection = (courseId) => {
    setSelectedCourses((prev) => (
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId]
    ));
  };

  const selectAllVisibleCourses = () => {
    setSelectedCourses((prev) => Array.from(new Set([...prev, ...filteredCourses.map((course) => course.id)])));
  };

  const clearSelectedCourses = () => {
    setSelectedCourses([]);
  };

  const filteredCourses = useMemo(() => {
    const search = String(courseSearch || '').trim().toLowerCase();
    return availableCourses.filter((course) => {
      const matchesSearch = !search || String(course?.name || '').toLowerCase().includes(search);
      const matchesSelection = !showOnlySelectedCourses || selectedCourses.includes(course.id);
      return matchesSearch && matchesSelection;
    });
  }, [availableCourses, courseSearch, selectedCourses, showOnlySelectedCourses]);

  useEffect(() => {
    if (showManual && autoEnroll) {
      fetchResources();
    }
  }, [showManual, autoEnroll]);

  const fetchResources = async () => {
    setLoadingResources(true);
    try {
      const { data: coursesData } = await supabase
        .from('courses')
        .select('id, name')
        .eq('status', 'Ativo')
        .order('name');
        
      const { data: classesData } = await supabase
        .from('classes')
        .select('id, name')
        .in('status', ['Ativo', 'Programada'])
        .order('name');

      setAvailableCourses(coursesData || []);
      setAvailableClasses(classesData || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoadingResources(false);
    }
  };

  const updateManualPrimaryCompany = (nextCompanyId) => {
    setManualForm((prev) => {
      const companyId = String(nextCompanyId || '');
      if (prev.profile !== 'Ponto Focal') {
        return { ...prev, companyId };
      }
      return {
        ...prev,
        companyId,
        companyIds: normalizeLinkedCompanyIds(companyId, prev.companyIds),
      };
    });
  };

  const updateManualLinkedCompanies = (nextCompanyIds) => {
    setManualForm((prev) => {
      const normalizedIds = normalizeLinkedCompanyIds(prev.companyId, nextCompanyIds);
      const nextPrimary = normalizedIds.includes(String(prev.companyId || ''))
        ? String(prev.companyId || '')
        : (normalizedIds[0] || '');

      return {
        ...prev,
        companyId: nextPrimary,
        companyIds: normalizeLinkedCompanyIds(nextPrimary, normalizedIds),
      };
    });
  };

  useEffect(() => {
    let cancelled = false;

    const loadCompanyCatalog = async () => {
      if (!showManual) return;
      const companyId = String(manualForm.companyId || '').trim();
      if (!companyId) {
        setCompanyCargos([]);
        setCompanyAreas([]);
        return;
      }

      try {
        const [{ data: cargosData }, { data: areasData }] = await Promise.all([
          supabase.from('company_cargos').select('id, name').eq('company_id', companyId).order('name'),
          supabase.from('company_areas').select('id, name').eq('company_id', companyId).order('name')
        ]);
        if (cancelled) return;
        setCompanyCargos(cargosData || []);
        setCompanyAreas(areasData || []);
        const validCargos = new Set((cargosData || []).map((item) => String(item.name || '').trim()));
        const validAreas = new Set((areasData || []).map((item) => String(item.name || '').trim()));
        setManualForm((prev) => {
          const nextCargo = prev.cargo && validCargos.has(String(prev.cargo).trim()) ? prev.cargo : '';
          const nextArea = prev.area && validAreas.has(String(prev.area).trim()) ? prev.area : '';
          if (nextCargo === prev.cargo && nextArea === prev.area) return prev;
          return { ...prev, cargo: nextCargo, area: nextArea };
        });
      } catch (error) {
        console.error('Error fetching company cargos/areas:', error);
        if (!cancelled) {
          setCompanyCargos([]);
          setCompanyAreas([]);
        }
      }
    };

    loadCompanyCatalog();
    return () => { cancelled = true; };
  }, [showManual, manualForm.companyId]);

  const updateManualProfile = (nextProfile) => {
    setManualForm((prev) => {
      if (nextProfile === 'Ponto Focal') {
        return {
          ...prev,
          profile: nextProfile,
          companyIds: normalizeLinkedCompanyIds(prev.companyId, prev.companyIds),
        };
      }

      return {
        ...prev,
        profile: nextProfile,
        companyIds: [],
      };
    });
  };

  const reloadUsers = async () => {
    setLoading(true);
    try {
      const currentSessionUserId = auth?.session?.user?.id || null;
      const focalScopeCompanyIds = normalizeLinkedCompanyIds(effectiveUserCompanyId, auth?.profile?.companyIds || []);
      const attachScheduledInviteData = async (rows) => {
        const normalizedRows = Array.isArray(rows) ? rows : [];
        const userIds = normalizedRows
          .map((row) => String(row?.id || '').trim())
          .filter(Boolean);

        if (userIds.length === 0) {
          return normalizedRows;
        }

        const scheduledInviteMap = new Map();
        const scheduledInviteHistoryMap = new Map();
        const chunkSize = 100;

        for (let offset = 0; offset < userIds.length; offset += chunkSize) {
          const chunk = userIds.slice(offset, offset + chunkSize);
          const { data, error } = await supabase
            .from('communication_queue')
            .select('recipient_id, scheduled_for, payload, status, last_error, processed_at, created_at, attempts')
            .in('recipient_id', chunk);

          if (error) throw error;

          for (const job of (data || [])) {
            if (String(job?.payload?.type || '') !== 'user_invite_schedule') continue;
            const recipientId = String(job?.recipient_id || '').trim();
            const scheduledFor = job?.scheduled_for || null;
            if (!recipientId) continue;

            const previousHistory = scheduledInviteHistoryMap.get(recipientId);
            const previousHistoryTime = previousHistory?.scheduled_for
              ? new Date(previousHistory.scheduled_for).getTime()
              : new Date(previousHistory?.created_at || 0).getTime();
            const currentHistoryTime = scheduledFor
              ? new Date(scheduledFor).getTime()
              : new Date(job?.created_at || 0).getTime();

            if (!previousHistory || currentHistoryTime > previousHistoryTime) {
              scheduledInviteHistoryMap.set(recipientId, {
                scheduled_for: scheduledFor,
                original_scheduled_for: job?.payload?.original_scheduled_for || null,
                status: job?.status || null,
                last_error: job?.last_error || null,
                processed_at: job?.processed_at || null,
                created_at: job?.created_at || null,
                attempts: Number(job?.attempts || 0),
              });
            }

            if (String(job?.status || '') !== 'pending' || !scheduledFor) continue;
            const previous = scheduledInviteMap.get(recipientId);
            if (!previous || new Date(scheduledFor).getTime() > new Date(previous.scheduled_for).getTime()) {
              scheduledInviteMap.set(recipientId, {
                scheduled_for: scheduledFor,
                original_scheduled_for: job?.payload?.original_scheduled_for || null,
                status: job?.status || null,
                last_error: job?.last_error || null,
                processed_at: job?.processed_at || null,
                created_at: job?.created_at || null,
                attempts: Number(job?.attempts || 0),
              });
            }
          }
        }

        return normalizedRows.map((row) => {
          const scheduleMeta = (
            scheduledInviteMap.get(String(row?.id || '').trim())
            || scheduledInviteHistoryMap.get(String(row?.id || '').trim())
            || {}
          );

          return {
            ...row,
            scheduled_invite_for: scheduleMeta.scheduled_for || null,
            scheduled_invite_original_for: scheduleMeta.original_scheduled_for || null,
            scheduled_invite_job_status: scheduleMeta.status || null,
            scheduled_invite_last_error: scheduleMeta.last_error || null,
            scheduled_invite_processed_at: scheduleMeta.processed_at || null,
            scheduled_invite_attempts: Number(scheduleMeta.attempts || 0),
          };
        });
      };

      const mapUserRow = (r) => {
        const effectiveLastSignInAt = r?.last_real_sign_in_at || r?.last_sign_in_at || null;
        const rawStatus = String(r?.status || '');
        const linkedCompanies = normalizeLinkedCompanies(r?.linked_companies, r?.company_id, r?.companies?.name || r?.company_name || '-');
        const linkedCompanyIds = normalizeLinkedCompanyIds(r?.company_id, r?.linked_company_ids || linkedCompanies.map((item) => item.id));
        // Em modo impersonação, o parceiro pode estar logado mesmo com status pendente.
        const displayStatus = (
          currentSessionUserId
          && String(r?.id || '') === String(currentSessionUserId)
          && ['Pendente', 'Programado'].includes(rawStatus)
        )
          ? 'Ativo'
          : rawStatus;

        return {
          id: r.id,
          name: r.full_name,
          email: r.email,
          cpf: r.cpf,
          whatsapp: r.whatsapp,
          cargo: r.cargo || '',
          area: r.area || '',
          profile: r.role,
          status: displayStatus,
          companyId: r.company_id,
          companyIds: linkedCompanyIds,
          linkedCompanies,
          company: buildCompanyLabel(linkedCompanies, r.companies?.name || r.company_name || '-'),
          partnerId: r.sas_partner_id,
          partner: r.sas_partners?.name || r.partner_name || '',
          created_at: r.created_at || null,
          last_sign_in_at: effectiveLastSignInAt,
          first_login_at: r.first_login_at || null,
          last_invite_sent_at: r.last_invite_sent_at || null,
          last_invite_expires_at: r.last_invite_expires_at || null,
          scheduled_invite_for: r.scheduled_invite_for || null,
          scheduled_invite_original_for: r.scheduled_invite_original_for || null,
          scheduled_invite_job_status: r.scheduled_invite_job_status || null,
          scheduled_invite_last_error: r.scheduled_invite_last_error || null,
          scheduled_invite_processed_at: r.scheduled_invite_processed_at || null,
          scheduled_invite_attempts: Number(r.scheduled_invite_attempts || 0),
        };
      };

      const scopeParams = {
        p_company_id: null,
        p_partner_id: isPartnerScopedRole(effectiveRole) ? (partner?.id || null) : null,
      };
      const { data: scopedRows, error: scopedError } = await supabase.rpc('admin_list_users_by_scope', scopeParams);
      if (!scopedError) {
        const rows = await attachScheduledInviteData(Array.isArray(scopedRows) ? scopedRows : []);
        setUsers(rows.map(mapUserRow));
        return;
      }

      if (isPartnerScopedRole(effectiveRole) || effectiveRole === 'Ponto Focal') {
        // Fallback imediato: usa RPC de matrículas (já existente) para não perder visibilidade dos alunos.
        const { data: enrollmentRows, error: enrollmentError } = await supabase.rpc('admin_list_enrollments_by_scope', scopeParams);
        if (enrollmentError) throw scopedError;

        const uniqueStudents = new Map();
        for (const row of (enrollmentRows || [])) {
          const sid = row?.student_id;
          if (!sid || uniqueStudents.has(String(sid))) continue;
          uniqueStudents.set(String(sid), {
            id: sid,
            email: row?.student_email || '',
            full_name: row?.student_full_name || row?.student_email || 'Aluno',
            cpf: null,
            whatsapp: null,
            role: 'Aluno',
            status: 'Ativo',
            last_sign_in_at: null,
            company_id: null,
            linked_company_ids: [],
            linked_companies: [],
            sas_partner_id: isPartnerScopedRole(effectiveRole) ? (partner?.id || null) : null,
            created_at: row?.created_at || null,
            company_name: '-',
            partner_name: partner?.name || '',
          });
        }
        const fallbackRows = await attachScheduledInviteData(Array.from(uniqueStudents.values()));
        setUsers(fallbackRows.map(mapUserRow));
        return;
      }

      let query = supabase
        .from('profiles')
        .select('id, email, full_name, cpf, whatsapp, cargo, area, role, status, last_sign_in_at, company_id, sas_partner_id, created_at, companies:companies!profiles_company_id_fkey(name), sas_partners(name)')
        .order('created_at', { ascending: false });

      if (isPartnerScopedRole(effectiveRole) && partner?.id) {
        const [profilesScopeRes, classesScopeRes] = await Promise.all([
          supabase
            .from('profiles')
            .select('company_id')
            .eq('sas_partner_id', partner.id)
            .not('company_id', 'is', null),
          supabase
            .from('classes')
            .select('company_id')
            .eq('partner_id', partner.id)
            .not('company_id', 'is', null),
        ]);

        if (profilesScopeRes.error) throw profilesScopeRes.error;
        if (classesScopeRes.error) throw classesScopeRes.error;

        const companyIdsFromProfiles = (profilesScopeRes.data || []).map((row) => row.company_id).filter(Boolean);
        const companyIdsFromClasses = (classesScopeRes.data || []).map((row) => row.company_id).filter(Boolean);
        const allowedCompanyIds = Array.from(new Set([...companyIdsFromProfiles, ...companyIdsFromClasses]));

        if (allowedCompanyIds.length > 0) {
          query = query.or(`sas_partner_id.eq.${partner.id},company_id.in.(${allowedCompanyIds.join(',')})`);
        } else {
          query = query.eq('sas_partner_id', partner.id);
        }
      }

      if (effectiveRole === 'Ponto Focal' && focalScopeCompanyIds.length > 0) {
        query = query.in('company_id', focalScopeCompanyIds);
      }

      const { data, error } = await query;
      if (error) throw error;
      const rows = await attachScheduledInviteData(Array.isArray(data) ? data : []);
      setUsers(rows.map(mapUserRow));
    } catch (err) {
      setUsers([]);
      setNotification({ message: String(err?.message || err || 'Falha ao carregar usuários.'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadUsers();
  }, [effectiveRole, partner?.id, effectiveUserCompanyId, auth?.profile?.companyIds?.join(',')]);

  const reloadLogs = async (searchTerm = logsSearch) => {
    if (!canViewUserLogs) return;
    setLogsLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_list_user_logs_by_scope', {
        p_limit: 100,
        p_offset: 0,
        p_search: searchTerm?.trim() ? searchTerm.trim() : null,
      });
      if (error) throw error;
      setUserLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      setUserLogs([]);
      setNotification({ message: String(err?.message || err || 'Falha ao carregar logs de usuários.'), type: 'error' });
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'logs' || !canViewUserLogs) return;
    reloadLogs();
  }, [activeTab, canViewUserLogs, logsSearch, effectiveRole, partner?.id, effectiveUserCompanyId, auth?.profile?.companyIds?.join(',')]);

  const visibleUsers = useMemo(() => {
    let arr = Array.isArray(users) ? users : [];
    const term = q.trim().toLowerCase();
    if (term) {
      arr = arr.filter(u =>
        (u.name || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term)
      );
    }
    if (Array.isArray(roleFilter) && roleFilter.length > 0) {
      const setRoles = new Set(roleFilter.map(String));
      arr = arr.filter(u => setRoles.has(String(u.profile || '')));
    }
    if (Array.isArray(statusFilter) && statusFilter.length > 0) {
      const setStatuses = new Set(statusFilter.map(String));
      arr = arr.filter(u => setStatuses.has(String(u.status || '')));
    }
    if (effectiveRole === 'Admin' && Array.isArray(partnerFilter) && partnerFilter.length > 0) {
      const setIds = new Set(partnerFilter.map(String));
      arr = arr.filter((u) => setIds.has(String(u.partnerId || '')));
    }
    if (Array.isArray(companyFilter) && companyFilter.length > 0) {
      const setIds = new Set(companyFilter.map(String));
      arr = arr.filter((u) => {
        const linkedIds = normalizeLinkedCompanyIds(u.companyId, u.companyIds || []);
        return linkedIds.some((companyId) => setIds.has(String(companyId)));
      });
    }
    const normalizedSortField = String(sortConfig?.field || 'activation');
    const direction = sortConfig?.direction === 'asc' ? 1 : -1;
    return arr
      .map((user, index) => ({ user, index }))
      .sort((a, b) => {
        if (normalizedSortField === 'name' || normalizedSortField === 'company') {
          const aValue = String(
            normalizedSortField === 'name'
              ? (a.user?.name || a.user?.email || '')
              : (a.user?.company || '')
          ).trim();
          const bValue = String(
            normalizedSortField === 'name'
              ? (b.user?.name || b.user?.email || '')
              : (b.user?.company || '')
          ).trim();
          const compare = aValue.localeCompare(bValue, 'pt-BR', { sensitivity: 'base' });
          if (compare !== 0) return compare * direction;
          return a.index - b.index;
        }

        const aTime = a.user?.first_login_at ? new Date(a.user.first_login_at).getTime() : Number.NaN;
        const bTime = b.user?.first_login_at ? new Date(b.user.first_login_at).getTime() : Number.NaN;
        const aValid = Number.isFinite(aTime);
        const bValid = Number.isFinite(bTime);

        if (aValid && bValid && aTime !== bTime) return (aTime - bTime) * direction;
        if (aValid !== bValid) return aValid ? -1 : 1;
        return a.index - b.index;
      })
      .map(({ user }) => user);
  }, [users, q, effectiveRole, roleFilter, statusFilter, partnerFilter, companyFilter, sortConfig]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const totalPages = Math.max(1, Math.ceil(visibleUsers.length / itemsPerPage));

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return visibleUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [visibleUsers, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [q, roleFilter, statusFilter, partnerFilter, companyFilter, sortConfig, itemsPerPage]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const toggleSort = (field, defaultDirection = 'asc') => {
    setSortConfig((current) => {
      if (current?.field === field) {
        return {
          field,
          direction: current.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        field,
        direction: defaultDirection,
      };
    });
  };

  const getSortButtonLabel = (field) => {
    if (field === 'activation') return sortConfig?.direction === 'desc' ? 'Recentes' : 'Antigas';
    return sortConfig?.direction === 'asc' ? 'A/Z' : 'Z/A';
  };

  const getSortButtonTitle = (field) => {
    if (field === 'name') {
      return `Classificar por nome (${sortConfig?.field === 'name' && sortConfig?.direction === 'desc' ? 'Z para A' : 'A para Z'})`;
    }
    if (field === 'company') {
      return `Classificar por empresa (${sortConfig?.field === 'company' && sortConfig?.direction === 'desc' ? 'Z para A' : 'A para Z'})`;
    }
    return `Classificar por data de ativação (${sortConfig?.field === 'activation' && sortConfig?.direction === 'asc' ? 'mais antigas primeiro' : 'mais recentes primeiro'})`;
  };

  const getSortButtonClassName = (field) => {
    const isActive = sortConfig?.field === field;
    return `inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-bold normal-case transition ${
      isActive
        ? 'border-teal-500/60 bg-teal-500/10 text-teal-300 shadow-sm shadow-teal-950/40'
        : 'border-slate-700 bg-slate-900/80 text-slate-300 hover:border-slate-500 hover:text-white'
    }`;
  };

  const usersSummary = useMemo(() => {
    const roleCounts = new Map();
    const statusCounts = new Map();

    visibleUsers.forEach((user) => {
      const profileLabel = String(user?.profile || '').trim() || 'Sem perfil';
      const statusLabel = String(user?.status || '').trim() || 'Sem status';

      roleCounts.set(profileLabel, (roleCounts.get(profileLabel) || 0) + 1);
      statusCounts.set(statusLabel, (statusCounts.get(statusLabel) || 0) + 1);
    });

    const roleOrder = new Map(rolesList.map((item, index) => [item, index]));
    const roleBreakdown = Array.from(roleCounts, ([label, total]) => ({ label, total }))
      .sort((a, b) => {
        const aIndex = roleOrder.has(a.label) ? roleOrder.get(a.label) : Number.MAX_SAFE_INTEGER;
        const bIndex = roleOrder.has(b.label) ? roleOrder.get(b.label) : Number.MAX_SAFE_INTEGER;
        if (aIndex !== bIndex) return aIndex - bIndex;
        return a.label.localeCompare(b.label, 'pt-BR');
      });

    const statusBreakdown = Array.from(statusCounts, ([label, total]) => ({ label, total }))
      .sort((a, b) => {
        const aIndex = SUMMARY_STATUS_ORDER.indexOf(a.label);
        const bIndex = SUMMARY_STATUS_ORDER.indexOf(b.label);
        const normalizedAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const normalizedBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        if (normalizedAIndex !== normalizedBIndex) return normalizedAIndex - normalizedBIndex;
        return a.label.localeCompare(b.label, 'pt-BR');
      });

    return {
      totalUsers: visibleUsers.length,
      roleCounts,
      statusCounts,
      roleBreakdown,
      statusBreakdown,
    };
  }, [visibleUsers, rolesList]);

  const summaryCards = useMemo(() => (
    SUMMARY_CARD_ORDER.map((item) => {
      if (item.key === 'totalUsers') {
        return {
          ...item,
          value: usersSummary.totalUsers,
        };
      }
      if (item.key.startsWith('status:')) {
        const status = item.key.slice('status:'.length);
        return {
          ...item,
          value: usersSummary.statusCounts.get(status) || 0,
        };
      }
      if (item.key.startsWith('role:')) {
        const roleName = item.key.slice('role:'.length);
        return {
          ...item,
          value: usersSummary.roleCounts.get(roleName) || 0,
        };
      }
      return {
        ...item,
        value: 0,
      };
    })
  ), [usersSummary]);

  const companyOptions = useMemo(() => {
    const map = new Map();
    (users || []).forEach(u => {
      normalizeLinkedCompanies(u?.linkedCompanies, u?.companyId, u?.company).forEach((company) => {
        if (!company?.id) return;
        if (!map.has(String(company.id))) map.set(String(company.id), company.name || '-');
      });
    });
    return Array.from(map, ([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' }));
  }, [users]);

  const editableCompanies = useMemo(() => {
    if (!isScopedUserManager) {
      return Array.isArray(companies) ? companies : [];
    }

    const allowedCompanyIds = new Set();
    if (effectiveRole === 'Ponto Focal') {
      normalizeLinkedCompanyIds(effectiveUserCompanyId, auth?.profile?.companyIds || []).forEach((id) => {
        if (id) allowedCompanyIds.add(String(id));
      });
    }

    (users || []).forEach((user) => {
      normalizeLinkedCompanies(user?.linkedCompanies, user?.companyId, user?.company).forEach((company) => {
        if (company?.id) allowedCompanyIds.add(String(company.id));
      });
    });

    return (Array.isArray(companies) ? companies : [])
      .filter((company) => allowedCompanyIds.size === 0 || allowedCompanyIds.has(String(company?.id || '')))
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'pt-BR', { sensitivity: 'base' }));
  }, [isScopedUserManager, companies, effectiveRole, effectiveUserCompanyId, auth?.profile?.companyIds, users]);

  const partnerOptions = useMemo(() => {
    const map = new Map();
    (partners || []).forEach((item) => {
      const id = String(item?.id || '').trim();
      if (!id) return;
      map.set(id, item?.name || '-');
    });
    (users || []).forEach((user) => {
      const id = String(user?.partnerId || '').trim();
      if (!id) return;
      if (!map.has(id)) map.set(id, user?.partner || '-');
    });
    return Array.from(map, ([id, name]) => ({ id, name }))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' }));
  }, [partners, users]);

  const editablePartnerOptions = useMemo(() => {
    if (effectiveRole !== 'Parceiro SAS') return Array.isArray(partners) ? partners : [];
    return (Array.isArray(partners) ? partners : []).filter((item) => String(item?.id || '') === String(effectivePartnerId || ''));
  }, [effectiveRole, partners, effectivePartnerId]);

  const roleOptions = useMemo(() => {
    const roles = Array.from(new Set((users || []).map(u => String(u.profile || '').trim()).filter(Boolean)));
    return roles
      .map((r) => ({ id: r, name: r }))
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', { sensitivity: 'base' }));
  }, [users]);

  const statusOptions = useMemo(() => [
    { id: 'Ativo', name: 'Ativo' },
    { id: 'Programado', name: 'Programado' },
    { id: 'Pendente', name: 'Pendente' },
    { id: 'Inativo', name: 'Inativo' },
  ], []);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(USERS_FILTERS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setQ(typeof parsed?.q === 'string' ? parsed.q : '');
        setRoleFilter(Array.isArray(parsed?.roleFilter) ? parsed.roleFilter.map(String) : (typeof parsed?.roleFilter === 'string' && parsed.roleFilter ? [parsed.roleFilter] : []));
        setStatusFilter(Array.isArray(parsed?.statusFilter) ? parsed.statusFilter.map(String) : (typeof parsed?.statusFilter === 'string' && parsed.statusFilter ? [parsed.statusFilter] : []));
        setCompanyFilter(Array.isArray(parsed?.companyFilter) ? parsed.companyFilter.map(String) : []);
        setPartnerFilter(Array.isArray(parsed?.partnerFilter) ? parsed.partnerFilter.map(String) : []);
      }
    } catch {}
    finally {
      setFiltersHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!filtersHydrated) return;
    const normalizedPartnerFilter = effectiveRole === 'Admin'
      ? (Array.isArray(partnerFilter) ? partnerFilter.map(String) : [])
      : [];
    try {
      window.localStorage.setItem(
        USERS_FILTERS_STORAGE_KEY,
        JSON.stringify({
          q,
          roleFilter,
          statusFilter,
          partnerFilter: normalizedPartnerFilter,
          companyFilter: (companyFilter || []).map(String),
        }),
      );
    } catch {}
  }, [filtersHydrated, q, effectiveRole, roleFilter, statusFilter, partnerFilter, companyFilter]);

  useEffect(() => {
    if (effectiveRole === 'Admin') return;
    setPartnerFilter([]);
  }, [effectiveRole]);

  useEffect(() => {
    if (canManageRoleDefinitions || activeTab !== 'perfis') return;
    setActiveTab('geral');
  }, [canManageRoleDefinitions, activeTab, setActiveTab]);

  useEffect(() => {
    if (canViewUserLogs || activeTab !== 'logs') return;
    setActiveTab('geral');
  }, [canViewUserLogs, activeTab, setActiveTab]);

  useEffect(() => {
    if (!manualDraftHydrated) return;
    try {
      if (!showManual) {
        clearManualModalDraftStorage();
        return;
      }
      window.localStorage.setItem(
        MANUAL_MODAL_DRAFT_STORAGE_KEY,
        JSON.stringify({
          showManual: true,
          editingId,
          externalPrefill,
          manualForm,
          autoEnroll,
          selectedCourses,
          selectedClass,
          validityDays,
        }),
      );
    } catch {}
  }, [
    manualDraftHydrated,
    showManual,
    editingId,
    externalPrefill,
    manualForm,
    autoEnroll,
    selectedCourses,
    selectedClass,
    validityDays,
  ]);

  const formatDateTimeLabel = (value) => {
    if (!value) return 'Não registrado';
    try {
      return new Date(value).toLocaleString('pt-BR');
    } catch {
      return 'Não registrado';
    }
  };

  const getStatusDateMeta = (user) => {
    const normalizedStatus = String(user?.status || '').trim().toLowerCase();
    if (normalizedStatus === 'programado') {
      const hasScheduledDispatchIssue = Boolean(
        !user?.last_invite_sent_at
        && user?.scheduled_invite_last_error
      );
      const isRetryScheduled = (
        hasScheduledDispatchIssue
        && String(user?.scheduled_invite_job_status || '').toLowerCase() === 'pending'
        && user?.scheduled_invite_for
      );
      const inferredLastAttemptAt = (
        !user?.scheduled_invite_processed_at
        && isRetryScheduled
        && Number(user?.scheduled_invite_attempts || 0) > 0
      )
        ? new Date(
            new Date(user.scheduled_invite_for).getTime()
            - (Math.max(1, Number(user.scheduled_invite_attempts || 0)) * 15 * 60 * 1000)
          ).toISOString()
        : null;
      const items = [];

      if (hasScheduledDispatchIssue) {
        items.push({
          label: 'Agendamento original',
          value: user?.scheduled_invite_original_for
            ? `${formatSaoPauloDateTimeLabel(user?.scheduled_invite_original_for)} (SP)`
            : 'Não registrado',
        });

        items.push({
          label: 'Última tentativa',
          value: user?.scheduled_invite_processed_at
            ? `${formatSaoPauloDateTimeLabel(user?.scheduled_invite_processed_at)} (SP)`
            : inferredLastAttemptAt
              ? `${formatSaoPauloDateTimeLabel(inferredLastAttemptAt)} (SP)`
            : 'Não registrado',
        });

        items.push({
          label: 'Próxima tentativa',
          value: user?.scheduled_invite_for
            ? `${formatSaoPauloDateTimeLabel(user?.scheduled_invite_for)} (SP)`
            : 'Não agendada',
        });
      } else {
        items.push({
          label: 'Convite programado para',
          value: (user?.scheduled_invite_original_for || user?.scheduled_invite_for)
            ? `${formatSaoPauloDateTimeLabel(user?.scheduled_invite_original_for || user?.scheduled_invite_for)} (SP)`
            : 'Não registrado',
        });
      }

      return items;
    }
    if (normalizedStatus === 'pendente') {
      const items = [
        {
          label: 'Último convite',
          value: formatDateTimeLabel(user?.last_invite_sent_at || null),
        },
        {
          label: 'Convite expira em',
          value: formatDateTimeLabel(user?.last_invite_expires_at || null),
        },
      ];

      if (user?.scheduled_invite_for) {
        items.unshift({
          label: 'Convite programado para',
          value: `${formatSaoPauloDateTimeLabel(user?.scheduled_invite_for)} (SP)`,
        });
      }

      return items;
    }
    if (normalizedStatus === 'ativo') {
      return [
        {
          label: 'Ativado em',
          value: formatDateTimeLabel(user?.first_login_at || null),
        },
      ];
    }
    return null;
  };

  const clearUsersFilters = () => {
    setQ('');
    setRoleFilter([]);
    setStatusFilter([]);
    setCompanyFilter([]);
    setPartnerFilter([]);
    try {
      window.localStorage.removeItem(USERS_FILTERS_STORAGE_KEY);
    } catch {}
  };

  const visibleUserIdsSet = useMemo(() => {
    const s = new Set();
    (visibleUsers || []).forEach((u) => { if (u?.id) s.add(String(u.id)); });
    return s;
  }, [visibleUsers]);

  const selectedUserIdsSet = useMemo(() => {
    const s = new Set();
    (selectedUserIds || []).forEach((id) => { if (id) s.add(String(id)); });
    return s;
  }, [selectedUserIds]);

  const allUsersSelected = useMemo(() => {
    if (visibleUserIdsSet.size === 0) return false;
    for (const id of visibleUserIdsSet) if (!selectedUserIdsSet.has(id)) return false;
    return true;
  }, [visibleUserIdsSet, selectedUserIdsSet]);

  const someUsersSelected = useMemo(() => {
    if (selectedUserIdsSet.size === 0) return false;
    return !allUsersSelected;
  }, [selectedUserIdsSet, allUsersSelected]);

  useEffect(() => {
    setSelectedUserIds((prev) => {
      const filtered = (prev || []).filter((id) => visibleUserIdsSet.has(String(id)));
      return filtered.length === (prev?.length || 0) ? prev : filtered;
    });
  }, [visibleUserIdsSet]);

  const toggleSelectAllUsers = () => {
    if (allUsersSelected) {
      setSelectedUserIds((prev) => (prev || []).filter((id) => !visibleUserIdsSet.has(String(id))));
    } else {
      const next = new Set(selectedUserIdsSet);
      for (const id of visibleUserIdsSet) next.add(id);
      setSelectedUserIds(Array.from(next));
    }
  };

  const toggleSelectOneUser = (userId) => {
    const id = String(userId || '');
    if (!id) return;
    setSelectedUserIds((prev) => {
      const s = new Set((prev || []).map(String));
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return Array.from(s);
    });
  };

  const clearUserSelection = () => {
    setSelectedUserIds([]);
  };

  const getSelectedUsers = () => {
    const idSet = selectedUserIdsSet;
    return (users || []).filter((u) => idSet.has(String(u?.id || '')));
  };

  const closeSelectedResendModal = () => {
    if (selectedResendSubmitting) return;
    setSelectedResendModal((prev) => ({ ...prev, isOpen: false }));
  };

  const toggleSelectedResendChannel = (channel) => {
    if (isEmailOnlyInviteRole && channel !== 'email') return;
    setSelectedResendModal((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: !prev.channels[channel],
      },
    }));
  };

  const handleSelectedResendInvites = async () => {
    const targets = getSelectedUsers().filter((u) => canScopedUserResendInvite(u) && u?.email);
    const channels = selectedResendModal.channels || {};
    if (!channels.email && !channels.sms && !channels.whatsapp) {
      setNotification({ message: 'Selecione pelo menos um canal para reenviar em massa.', type: 'warning' });
      return;
    }
    if (targets.length === 0) {
      setNotification({ message: 'Nenhum usuário elegível entre os selecionados para reenvio de convite.', type: 'warning' });
      return;
    }

    setSelectedResendSubmitting(true);
    let successCount = 0;
    let failedCount = 0;

    try {
      const chunkSize = 5;
      for (let i = 0; i < targets.length; i += chunkSize) {
        const chunk = targets.slice(i, i + chunkSize);
        const results = await Promise.allSettled(
          chunk.map((user) =>
            invokeAdminManageUser({
              action: 'resend_invite',
              payload: {
                id: user.id || null,
                email: user.email,
                full_name: user.name || user.full_name || '',
                sas_partner_id: user.partnerId || user.sas_partner_id || null,
                company_id: user.companyId || user.company_id || null,
                role: user.profile || user.role || 'Aluno',
                status: user.status || 'Pendente',
                whatsapp: user.whatsapp || null,
                channels: {
                  email: Boolean(channels.email),
                  sms: Boolean(channels.sms),
                  whatsapp: Boolean(channels.whatsapp),
                },
              },
            })
          )
        );

        for (const result of results) {
          if (result.status !== 'fulfilled') {
            failedCount += 1;
            continue;
          }
          if (result.value?.error) {
            failedCount += 1;
            continue;
          }
          const dispatch = result.value?.data?.invite_channels_dispatch || null;
          if (dispatch?.ok === true) successCount += 1;
          else failedCount += 1;
        }
      }

      const type = failedCount > 0 ? 'warning' : 'success';
      setNotification({
        message:
          failedCount > 0
            ? `Reenvio em massa concluído com pendências: ${successCount} sucesso(s) e ${failedCount} falha(s).`
            : `Reenvio em massa concluído: ${successCount} convite(s) reenviado(s) com sucesso.`,
        type,
      });
      closeSelectedResendModal();
      if (canViewUserLogs) await reloadLogs();
    } catch (err) {
      console.error('Selected users bulk resend invite error:', err);
      setNotification({ message: 'Falha no reenvio em massa de convites.', type: 'error' });
    } finally {
      setSelectedResendSubmitting(false);
    }
  };

  const closeBulkPasswordResetModal = () => {
    if (bulkPasswordResetSubmitting) return;
    setBulkPasswordResetModal({ isOpen: false });
  };

  const handleBulkPasswordReset = async () => {
    const targets = getSelectedUsers().filter((u) => u?.email);
    if (targets.length === 0) {
      setNotification({ message: 'Nenhum usuário selecionado elegível para envio do link de senha.', type: 'warning' });
      return;
    }

    setBulkPasswordResetSubmitting(true);
    let successCount = 0;
    let failedCount = 0;

    try {
      const chunkSize = 5;
      for (let i = 0; i < targets.length; i += chunkSize) {
        const chunk = targets.slice(i, i + chunkSize);
        const results = await Promise.allSettled(
          chunk.map((user) =>
            invokeAdminManageUser({
              action: 'send_password_reset',
              payload: {
                id: user.id || null,
                email: user.email,
                sas_partner_id: user.partnerId || user.sas_partner_id || null,
              },
            }).then((res) => {
              if (res?.error) throw res.error;
              return res;
            })
          )
        );
        for (const result of results) {
          if (result.status === 'fulfilled') successCount += 1;
          else failedCount += 1;
        }
      }
      const type = failedCount > 0 ? 'warning' : 'success';
      setNotification({
        message:
          failedCount > 0
            ? `Envio em massa concluído com pendências: ${successCount} sucesso(s) e ${failedCount} falha(s).`
            : `Envio em massa concluído: ${successCount} link(s) de alteração de senha enviado(s) com sucesso.`,
        type,
      });
      closeBulkPasswordResetModal();
      if (canViewUserLogs) await reloadLogs();
    } catch (err) {
      console.error('Bulk password reset error:', err);
      setNotification({ message: 'Falha no envio em massa dos links de alteração de senha.', type: 'error' });
    } finally {
      setBulkPasswordResetSubmitting(false);
    }
  };

  const closeBulkDeleteModal = () => {
    if (bulkDeleteSubmitting) return;
    setBulkDeleteModal({ isOpen: false });
  };

  const handleBulkDelete = async () => {
    const currentUserId = auth?.session?.user?.id || null;
    const targets = getSelectedUsers().filter((u) => currentUserId ? String(u?.id || '') !== String(currentUserId) : true);
    if (targets.length === 0) {
      setNotification({ message: 'Nenhum usuário elegível para exclusão em massa.', type: 'warning' });
      return;
    }

    setBulkDeleteSubmitting(true);
    let successCount = 0;
    let failedCount = 0;

    try {
      const chunkSize = 5;
      for (let i = 0; i < targets.length; i += chunkSize) {
        const chunk = targets.slice(i, i + chunkSize);
        const results = await Promise.allSettled(
          chunk.map((user) =>
            invokeAdminManageUser({
              action: 'delete',
              payload: { id: user.id },
            }).then((res) => {
              if (res?.error) throw res.error;
              if (res?.data?.error) throw new Error(String(res.data.error));
              return res;
            })
          )
        );
        for (const result of results) {
          if (result.status === 'fulfilled') successCount += 1;
          else failedCount += 1;
        }
      }
      const type = failedCount > 0 ? 'warning' : 'success';
      setNotification({
        message:
          failedCount > 0
            ? `Exclusão em massa concluída com pendências: ${successCount} sucesso(s) e ${failedCount} falha(s).`
            : `Exclusão em massa concluída: ${successCount} usuário(s) excluído(s) com sucesso.`,
        type,
      });
      closeBulkDeleteModal();
      await reloadUsers();
      setSelectedUserIds([]);
      if (canViewUserLogs) await reloadLogs();
    } catch (err) {
      console.error('Bulk delete error:', err);
      setNotification({ message: 'Falha na exclusão em massa de usuários.', type: 'error' });
    } finally {
      setBulkDeleteSubmitting(false);
    }
  };

  const handleExportUsersExcel = async () => {
    try {
      const formatLastAccessForExport = (u) => {
        if (AWAITING_INVITE_STATUSES.has(String(u?.status || '').trim())) return 'Nunca';
        return u?.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString('pt-BR') : 'Nunca';
      };
      await exportUsersToExcel(visibleUsers || [], { formatLastAccess: formatLastAccessForExport });
    } catch (e) {
      console.error('Export users Excel error:', e);
      setNotification({ message: 'Erro ao exportar planilha de usuários.', type: 'error' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const normalizedLinkedCompanyIds = normalizeLinkedCompanyIds(manualForm.companyId, manualForm.companyIds);
    const isEditingScheduledInvite = Boolean(
      editingId
      && manualForm.inviteStartMode === INVITE_START_MODES.scheduled
      && String(manualForm.status || '').trim().toLowerCase() === 'programado'
    );
    if (!manualForm.name.trim()) newErrors.name = 'Nome é obrigatório';
    if (!manualForm.email.trim()) newErrors.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(manualForm.email)) newErrors.email = 'E-mail inválido';
    
    // Para criação, senha não é obrigatória. Para edição, senha é opcional; se informada, validar.
    if (editingId) {
      if (manualForm.password || manualForm.confirmPassword) {
        const validationError = validateDirectPassword(manualForm.password, manualForm.confirmPassword);
        if (validationError) {
          if (validationError.includes('conferem')) newErrors.confirmPassword = validationError;
          else newErrors.password = validationError;
        }
      }
    }

    if (!editingId || isEditingScheduledInvite) {
      if (!manualForm.inviteStartMode) {
        newErrors.inviteStartMode = 'Selecione quando o convite deve iniciar';
      } else if (manualForm.inviteStartMode === INVITE_START_MODES.scheduled) {
        const parsedInviteStart = parseInviteStartInput(manualForm.inviteStartDate);
        if (!manualForm.inviteStartDate) {
          newErrors.inviteStartDate = 'Informe a data e hora do início programado';
        } else if (!parsedInviteStart) {
          newErrors.inviteStartDate = `Informe uma data e hora válidas no formato ${INVITE_START_PLACEHOLDER}`;
        } else if (new Date(parsedInviteStart.iso) < getSaoPauloNowDate()) {
          newErrors.inviteStartDate = 'Selecione uma data e hora futuras no horário de São Paulo';
        }
      }
    }

    if (!manualForm.companyId) newErrors.companyId = 'Empresa é obrigatória';
    if (manualForm.profile === 'Ponto Focal' && normalizedLinkedCompanyIds.length === 0) {
      newErrors.companyIds = 'Selecione ao menos uma empresa vinculada';
    }
    if (isPartnerScopedRole(manualForm.profile) && !manualForm.partnerId) newErrors.partnerId = 'Parceiro SAS é obrigatório';
    
    const cpfDigits = manualForm.cpf.replace(/\D/g, '');
    if (manualForm.cpf.trim() && cpfDigits.length !== 11) {
      newErrors.cpf = 'CPF deve ter 11 dígitos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (isScopedUserManager && !editingId) {
      setNotification({ message: 'Seu perfil pode editar usuários existentes, mas não criar novos usuários.', type: 'warning' });
      return;
    }
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const action = editingId ? 'update' : 'create';
      const linkedCompanyIds = manualForm.profile === 'Ponto Focal'
        ? normalizeLinkedCompanyIds(manualForm.companyId, manualForm.companyIds)
        : normalizeLinkedCompanyIds(manualForm.companyId, []);
      const effectiveStatus = editingId
        ? manualForm.status
        : (manualForm.inviteStartMode === INVITE_START_MODES.scheduled ? 'Programado' : 'Pendente');
      const shouldPersistScheduledInvite = (
        manualForm.inviteStartMode === INVITE_START_MODES.scheduled
        && AWAITING_INVITE_STATUSES.has(String(effectiveStatus || '').trim())
      );
      const actionPayload = {
        id: editingId,
        email: manualForm.email.trim().toLowerCase(),
        password: editingId ? (manualForm.password || undefined) : undefined,
        full_name: manualForm.name.trim(),
        cpf: manualForm.cpf.replace(/\D/g, ''),
        whatsapp: manualForm.whatsapp ? manualForm.whatsapp.replace(/\D/g, '') : null,
        cargo: manualForm.cargo || null,
        area: manualForm.area || null,
        role: manualForm.profile,
        status: effectiveStatus,
        company_id: manualForm.companyId,
        linked_company_ids: linkedCompanyIds,
        sas_partner_id: manualForm.partnerId,
        invite_start_mode: shouldPersistScheduledInvite
          ? manualForm.inviteStartMode
          : undefined,
        invite_start_at: shouldPersistScheduledInvite
          ? buildInviteStartAtIso(manualForm.inviteStartDate)
          : undefined,
      };

      let responseData = null;
      try {
        const { data, error } = await invokeAdminManageUser({
          action,
          payload: actionPayload,
        });

        if (error) {
          const status = error?.context?.status;
          if (status === 401) {
            let reason401 = '';
            try {
              const ctx = await error.context?.json();
              const detailValue = ctx?.details
                ? (typeof ctx.details === 'string' ? ctx.details : JSON.stringify(ctx.details))
                : '';
              const clientTokenInfo = error?.clientTokenInfo
                ? JSON.stringify(error.clientTokenInfo)
                : '';
              reason401 = String(detailValue || clientTokenInfo || ctx?.message || ctx?.error || '');
            } catch {
              try {
                const rawText = String(await error.context?.text?.() || '')
                const clientTokenInfo = error?.clientTokenInfo
                  ? JSON.stringify(error.clientTokenInfo)
                  : ''
                reason401 = String(clientTokenInfo || rawText || '')
              } catch {}
            }
            setNotification({
              message: `Sessão expirada ou token inválido. ${reason401 ? `Detalhe: ${reason401}` : ''}`.trim(),
              type: 'error',
            });
            return;
          }
          let reason = '';
          try {
            const ctx = await error.context?.json();
            const detailValue = ctx?.details
              ? (typeof ctx.details === 'string' ? ctx.details : JSON.stringify(ctx.details))
              : '';
            const stageValue = ctx?.stage ? `stage=${ctx.stage}` : '';
            reason = String([ctx?.message || ctx?.error || '', stageValue, detailValue].filter(Boolean).join(' | '));
          } catch {
            try {
              const txt = await error.context?.text();
              reason = String(txt || '');
            } catch {
              reason = '';
            }
          }
          const msg = reason || String(error.message || 'Falha ao processar operação.');
          setNotification({ message: `Erro do servidor: ${msg}`, type: 'error' });
          return;
        }

        responseData = data;
      } catch (err) {
        if (err?.code === 'SESSION_EXPIRED' || err?.code === 'INVALID_TOKEN' || err?.code === 'INVALID_SESSION') {
          console.warn('[AdminUsers] sem sessão ou token inválido no fluxo de create/update');
          setNotification({ message: 'Sessão expirada. Faça login novamente.', type: 'error' });
          return;
        }
        console.error(err);
        setNotification({ message: 'Erro de conexão ao convidar usuário.', type: 'error' });
        return;
      }

      // FORCE UPDATE PROFILE DIRECTLY TO ENSURE PERSISTENCE
      if (editingId) {
          const { error: directUpdateError } = await supabase.from('profiles').update({
              whatsapp: manualForm.whatsapp ? manualForm.whatsapp.replace(/\D/g, '') : null
          }).eq('id', editingId);
          
          if (directUpdateError) {
              console.warn("Direct profile update failed:", directUpdateError);
              // Not throwing here to avoid breaking the flow if the main action succeeded,
              // but logging it. If RLS allows, this fixes the issue immediately.
          }
      }
      
      let userId = responseData?.user_id || responseData?.user?.id || responseData?.id;
      if (!editingId && !userId && autoEnroll && selectedCourses.length > 0) {
        const normalizedEmail = manualForm.email.trim().toLowerCase();
        for (let attempt = 1; attempt <= 3; attempt += 1) {
          const { data: profileRow, error: profileErr } = await supabase
            .from('profiles')
            .select('id')
            .eq('email', normalizedEmail)
            .maybeSingle();
          if (!profileErr && profileRow?.id) {
            userId = profileRow.id;
            break;
          }
          if (attempt < 3) {
            await new Promise(resolve => window.setTimeout(resolve, 250));
          }
        }
      }

      // Ensure profile exists for newly created users (fallback if trigger didn’t run).
      // Só insere quando o profile realmente não existe: sobrescrever aqui desfaria
      // o status derivado no backend (ex.: Programado -> Pendente).
      if (!editingId && userId) {
        try {
          const { data: existingProfile, error: existingProfileErr } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', userId)
            .maybeSingle();
          if (existingProfileErr) console.warn('Profiles fallback check failed:', existingProfileErr);
          if (!existingProfileErr && !existingProfile?.id) {
            const { error: upsertErr } = await supabase.from('profiles').upsert({
              id: userId,
              email: manualForm.email.trim().toLowerCase(),
              full_name: manualForm.name.trim(),
              cpf: manualForm.cpf ? manualForm.cpf.replace(/\D/g, '') : null,
              company_id: manualForm.companyId,
              role: manualForm.profile,
              status: effectiveStatus,
              sas_partner_id: manualForm.partnerId || null,
              whatsapp: manualForm.whatsapp ? manualForm.whatsapp.replace(/\D/g, '') : null,
              cargo: manualForm.cargo || null,
              area: manualForm.area || null
            }, { onConflict: 'id' });
            if (upsertErr) console.warn('Profiles upsert fallback failed:', upsertErr);
          }
        } catch (upErr) {
          console.warn('Profiles upsert fallback exception:', upErr);
        }
      }

      // Auto Enroll logic
      if (!editingId && autoEnroll && selectedCourses.length > 0 && !userId) {
        setNotification({
          message: 'Usuário criado, mas não foi possível identificar o ID para executar a matrícula automática.',
          type: 'warning'
        });
      } else if (!editingId && autoEnroll && userId && selectedCourses.length > 0) {
        try {
          const { error: enrollError } = await supabase.rpc('admin_enroll_student', {
            p_student_id: userId,
            p_course_ids: selectedCourses,
            p_class_id: selectedClass || null,
            p_duration_days: parseInt(validityDays),
            p_start_date: manualForm.inviteStartMode === INVITE_START_MODES.scheduled
              ? buildInviteStartAtIso(manualForm.inviteStartDate)
              : null,
          });

          if (enrollError) {
             console.error('Enrollment error:', enrollError);
             setNotification({ 
               message: 'Usuário criado, mas houve erro na matrícula automática: ' + enrollError.message, 
               type: 'warning' 
             });
          }
        } catch (enrollErr) {
          console.error('Enrollment exception:', enrollErr);
        }
      }

      if (editingId && manualForm.password && manualForm.confirmPassword) {
        persistAdminPassword(editingId, manualForm.password, manualForm.confirmPassword);
      }

      if (editingId) {
        const inviteSchedule = responseData?.invite_schedule || null;
        setNotification({
          message: inviteSchedule?.mode === INVITE_START_MODES.scheduled && inviteSchedule?.scheduled_for
            ? `Usuário atualizado com sucesso! Convite reprogramado para ${formatSaoPauloDateTimeLabel(inviteSchedule.scheduled_for)} (horário de São Paulo).`
            : 'Usuário atualizado com sucesso!',
          type: 'success'
        });
      } else {
        const inviteSchedule = responseData?.invite_schedule || null;
        if (inviteSchedule?.mode === INVITE_START_MODES.scheduled) {
          const formattedDate = manualForm.inviteStartDate
            ? formatSaoPauloDateTimeLabel(buildInviteStartAtIso(manualForm.inviteStartDate))
            : 'data e hora programadas';
          setNotification({
            message: `Usuário criado com sucesso! O convite foi programado para ${formattedDate} (horário de São Paulo).`,
            type: 'success'
          });
          setShowManual(false);
          resetForm();
          await reloadUsers();
          if (canViewUserLogs) await reloadLogs();
          return;
        }

        const dispatch = responseData?.invite_channels_dispatch || null;
        const dispatchOk = dispatch?.ok === true;
        const dispatchSkipped = dispatch?.skipped === true;
        const dispatchReason = dispatch?.reason ? String(dispatch.reason) : '';
        const redirectTo = responseData?.redirect_to ? String(responseData.redirect_to) : '';

        if (dispatchOk) {
          setNotification({
            message: 'Usuário criado e convite enviado pelo sistema!',
            type: 'success'
          });
        } else {
          const reasonLabel = getInviteDispatchFeedback(dispatch, dispatchSkipped ? 'skipped' : 'failed');
          const linkLabel = redirectTo ? ` Link: ${redirectTo}` : '';
          setNotification({
            message: `Usuário criado, mas o convite NÃO foi enviado (${reasonLabel}).${linkLabel}`,
            type: 'warning'
          });
        }
      }
      setShowManual(false);
      resetForm();
      await reloadUsers();
      if (canViewUserLogs) await reloadLogs();
    } catch (err) {
      setNotification({ message: String(err?.message || err || 'Falha ao processar operação.'), type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    clearManualModalDraftStorage();
    setManualForm({ 
      name: '', 
      email: '',
      password: '',
      confirmPassword: '',
      companyId: '', 
      companyIds: [],
      cpf: '',
      whatsapp: '', 
      profile: 'Aluno', 
      partnerId: '', 
      status: 'Ativo',
      cargo: '',
      area: '',
      inviteStartMode: '',
      inviteStartDate: '',
    });
    setEditingId(null);
    setErrors({});
    setExternalPrefill(false);
    setManualPasswordShow(false);
    setManualPasswordConfirmShow(false);
    
    // Reset auto-enrollment
    setAutoEnroll(false);
    setSelectedCourses([]);
    setCourseSearch('');
    setShowOnlySelectedCourses(false);
    setSelectedClass('');
    setValidityDays(365);
    setValidityDays(365);
    setValidityDays(365);
  };

  const handleEdit = (user) => {
    const persistedPassword = getPersistedAdminPassword(user.id);
    const selectedCompanyIds = user.companyIds || user.linkedCompanyIds || [];
    const hasScheduledInvite = ['pendente', 'programado'].includes(String(user.status || '').trim().toLowerCase()) && !!user.scheduled_invite_for;
    setManualForm({
      name: user.name,
      email: user.email,
      password: persistedPassword?.password || '',
      confirmPassword: persistedPassword?.confirmPassword || '',
      companyId: user.companyId || '',
      companyIds: normalizeLinkedCompanyIds(user.companyId || '', selectedCompanyIds),
      cpf: user.cpf || '',
      whatsapp: user.whatsapp || '',
      cargo: user.cargo || '',
      area: user.area || '',
      profile: user.profile,
      partnerId: user.partnerId || effectivePartnerId || '',
      status: user.status || 'Ativo',
      inviteStartMode: hasScheduledInvite ? INVITE_START_MODES.scheduled : INVITE_START_MODES.immediate,
      inviteStartDate: hasScheduledInvite ? formatSaoPauloDateTimeForInput(user.scheduled_invite_for) : '',
    });
    setEditingId(user.id);
    setErrors({});
    setManualPasswordShow(false);
    setManualPasswordConfirmShow(false);
    setShowManual(true);
  };

  const closeManualModal = () => {
    clearManualModalDraftStorage();
    setManualPasswordShow(false);
    setManualPasswordConfirmShow(false);
    setShowManual(false);
  };

  const canEditScheduledInvite = Boolean(
    editingId
    && manualForm.inviteStartMode === INVITE_START_MODES.scheduled
    && String(manualForm.status || '').trim().toLowerCase() === 'programado'
  );

  const handleDelete = (id) => {
    const currentUserId = auth?.session?.user?.id || null;
    if (currentUserId && String(id) === String(currentUserId)) {
      setNotification({ message: 'Você não pode excluir sua própria conta.', type: 'error' });
      return;
    }
    setConfirmModal({
      isOpen: true,
      title: 'Excluir Usuário',
      message: 'Tem certeza que deseja excluir este usuário? Esta ação é irreversível e removerá o acesso do usuário ao sistema.',
      onConfirm: async () => {
        try {
          const { data, error } = await invokeAdminManageUser({
            action: 'delete',
            payload: { id },
          });
          
          if (error) {
            try {
              const ctx = await error.context?.json();
              const msg = ctx?.error || ctx?.message || ctx?.details || '';
              throw new Error(msg || `HTTP ${error?.context?.status || 400}`);
            } catch {
              try {
                const txt = await error.context?.text();
                throw new Error(txt || error.message || 'Falha ao excluir usuário.');
              } catch {
                throw new Error(error.message || 'Falha ao excluir usuário.');
              }
            }
          }
          if (data?.error) throw new Error(data.error);

          const profileDeleted = data?.profile_deleted !== false;
          const deleteMode = String(data?.delete_mode || '');
          const serverMessage = String(data?.message || '');
          setNotification({
            message: profileDeleted
              ? 'Usuário excluído com sucesso!'
              : (serverMessage || 'Usuário desativado (soft delete), mas com vínculos que impediram remoção total no banco.'),
            type: profileDeleted ? 'success' : (deleteMode === 'soft' ? 'warning' : 'success'),
          });
          await reloadUsers();
          if (canViewUserLogs) await reloadLogs();
        } catch (err) {
          if (err?.code === 'SESSION_EXPIRED' || err?.code === 'INVALID_TOKEN' || err?.code === 'INVALID_SESSION') {
            setNotification({ message: 'Sessão expirada. Faça login novamente.', type: 'error' });
            return;
          }
          setNotification({ message: String(err?.message || err || 'Falha ao excluir usuário.'), type: 'error' });
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const openResendInviteModal = (user) => {
    if (!canScopedUserResendInvite(user)) return;
    setResendInviteModal({
      isOpen: true,
      user,
      channels: {
        email: true,
        sms: !isEmailOnlyInviteRole,
        whatsapp: !isEmailOnlyInviteRole,
      },
    });
  };

  const openBulkResendModal = () => {
    setBulkResendModal({
      isOpen: true,
      channels: {
        email: true,
        sms: false,
        whatsapp: false,
      },
      statuses: {
        pending: false,
        active: true,
      },
      startDate: '',
      endDate: '',
    });
  };

  const closeBulkResendModal = () => {
    setBulkResendModal((prev) => ({ ...prev, isOpen: false }));
  };

  const toggleBulkResendChannel = (channel) => {
    setBulkResendModal((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: !prev.channels[channel],
      },
    }));
  };

  const toggleBulkResendStatus = (statusKey) => {
    setBulkResendModal((prev) => ({
      ...prev,
      statuses: {
        ...prev.statuses,
        [statusKey]: !prev.statuses[statusKey],
      },
    }));
  };

  const getBulkResendTargetUsers = () => {
    const source = Array.isArray(users) ? users : [];
    const startDate = String(bulkResendModal.startDate || '');
    const endDate = String(bulkResendModal.endDate || '');
    const statuses = bulkResendModal.statuses || {};
    const allowPending = Boolean(statuses.pending);
    const allowActive = Boolean(statuses.active);

    return source.filter((u) => {
      if (!u?.email) return false;
      const normalizedStatus = String(u?.status || '').trim().toLowerCase();
      const isPending = normalizedStatus === 'pendente';
      const isActive = normalizedStatus === 'ativo';
      if ((isPending && !allowPending) || (isActive && !allowActive)) return false;
      if (!isPending && !isActive) return false;
      const createdAtMs = Date.parse(String(u.created_at || ''));
      if (!Number.isFinite(createdAtMs)) return false;
      if (startDate && createdAtMs < new Date(`${startDate}T00:00:00`).getTime()) return false;
      if (endDate && createdAtMs > new Date(`${endDate}T23:59:59.999`).getTime()) return false;
      return true;
    });
  };

  const handleBulkResendInvites = async () => {
    const selectedChannels = bulkResendModal.channels || {};
    const selectedStatuses = bulkResendModal.statuses || {};
    if (!selectedChannels.email && !selectedChannels.sms && !selectedChannels.whatsapp) {
      setNotification({ message: 'Selecione pelo menos um canal para reenviar em massa.', type: 'warning' });
      return;
    }
    if (!selectedStatuses.pending && !selectedStatuses.active) {
      setNotification({ message: 'Selecione pelo menos um status para reenviar em massa.', type: 'warning' });
      return;
    }
    if (bulkResendModal.startDate && bulkResendModal.endDate && bulkResendModal.startDate > bulkResendModal.endDate) {
      setNotification({ message: 'A data inicial não pode ser posterior à data final.', type: 'warning' });
      return;
    }

    const targets = getBulkResendTargetUsers();
    if (targets.length === 0) {
      setNotification({ message: 'Nenhum usuário encontrado para o período selecionado.', type: 'warning' });
      return;
    }

    setBulkResendSubmitting(true);
    let successCount = 0;
    let failedCount = 0;

    try {
      const chunkSize = 5;
      for (let i = 0; i < targets.length; i += chunkSize) {
        const chunk = targets.slice(i, i + chunkSize);
        const results = await Promise.allSettled(
          chunk.map((user) =>
            invokeAdminManageUser({
              action: 'resend_invite',
              payload: {
                id: user.id || null,
                email: user.email,
                full_name: user.name || user.full_name || '',
                sas_partner_id: user.partnerId || user.sas_partner_id || null,
                company_id: user.companyId || user.company_id || null,
                role: user.profile || user.role || 'Aluno',
                status: user.status || 'Pendente',
                whatsapp: user.whatsapp || null,
                channels: {
                  email: Boolean(selectedChannels.email),
                  sms: Boolean(selectedChannels.sms),
                  whatsapp: Boolean(selectedChannels.whatsapp),
                },
              },
            })
          )
        );

        for (const result of results) {
          if (result.status !== 'fulfilled') {
            failedCount += 1;
            continue;
          }

          if (result.value?.error) {
            failedCount += 1;
            continue;
          }

          const dispatch = result.value?.data?.invite_channels_dispatch || null;
          if (dispatch?.ok === true) successCount += 1;
          else failedCount += 1;
        }
      }

      const type = failedCount > 0 ? 'warning' : 'success';
      setNotification({
        message:
          failedCount > 0
            ? `Reenvio em massa concluído com pendências: ${successCount} sucesso(s) e ${failedCount} falha(s).`
            : `Reenvio em massa concluído: ${successCount} convite(s) reenviado(s) com sucesso.`,
        type,
      });
      closeBulkResendModal();
    } catch (err) {
      console.error('Bulk resend invite error:', err);
      setNotification({ message: 'Falha no reenvio em massa de convites.', type: 'error' });
    } finally {
      setBulkResendSubmitting(false);
    }
  };

  const toggleResendChannel = (channel) => {
    if (isEmailOnlyInviteRole) return;
    setResendInviteModal((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: !prev.channels[channel],
      },
    }));
  };

  const closeResendInviteModal = () => {
    setResendInviteModal((prev) => ({ ...prev, isOpen: false, user: null }));
  };

  const handleResendInvite = async () => {
    const user = resendInviteModal.user;
    if (!user) return;

    const selectedChannels = isEmailOnlyInviteRole
      ? { email: true, sms: false, whatsapp: false }
      : (resendInviteModal.channels || {});
    if (!selectedChannels.email && !selectedChannels.sms && !selectedChannels.whatsapp) {
      setNotification({ message: 'Selecione pelo menos um canal para reenviar o convite.', type: 'warning' });
      return;
    }

    setResendSubmitting(true);
    try {
      const { data, error } = await invokeAdminManageUser({
        action: 'resend_invite',
        payload: {
          id: user.id || null,
          email: user.email,
          full_name: user.name || user.full_name || '',
          sas_partner_id: user.partnerId || user.sas_partner_id || null,
          company_id: user.companyId || user.company_id || null,
          role: user.profile || user.role || 'Aluno',
          status: user.status || 'Pendente',
          whatsapp: user.whatsapp || null,
          channels: {
            email: Boolean(selectedChannels.email),
            sms: Boolean(selectedChannels.sms),
            whatsapp: Boolean(selectedChannels.whatsapp),
          },
        },
      });
      if (error) {
        let reason = '';
        try {
          const ctx = await error.context?.json();
          const code = ctx?.error ? String(ctx.error) : '';
          const msg = ctx?.message ? String(ctx.message) : '';
            const details = ctx?.details ? String(ctx.details) : '';
            reason = (code && msg) ? `${code}: ${msg}${details ? ` | detalhe: ${details}` : ''}` : (msg || code || details || error.message || '');
        } catch { reason = error.message || ''; }
        throw new Error(reason || 'Falha desconhecida');
      }
      const dispatch = data?.invite_channels_dispatch || null;
      const dispatchOk = dispatch?.ok === true;
      const dispatchSkipped = dispatch?.skipped === true;
      const redirectTo = data?.redirect_to ? String(data.redirect_to) : '';

      if (!dispatchOk) {
        const reasonLabel = getInviteDispatchFeedback(dispatch, dispatchSkipped ? 'skipped' : 'failed');
        const linkLabel = redirectTo ? ` Link: ${redirectTo}` : '';
        setNotification({
          message: `Convite NÃO enviado (${reasonLabel}).${linkLabel}`,
          type: 'warning'
        });
        closeResendInviteModal();
        return;
      }

      setNotification({ message: 'Convite reenviado com sucesso.', type: 'success' });
      if (canViewUserLogs) await reloadLogs();
      closeResendInviteModal();
    } catch (err) {
      if (err?.code === 'SESSION_EXPIRED' || err?.code === 'INVALID_TOKEN' || err?.code === 'INVALID_SESSION') {
        setNotification({ message: 'Sessão expirada. Faça login novamente.', type: 'error' });
        return;
      }
      console.error('Resend invite error:', err);
      setNotification({ message: `Falha ao reenviar convite. ${err?.message ? String(err.message) : ''}`, type: 'error' });
    } finally {
      setResendSubmitting(false);
    }
  };

  const handleSendPasswordReset = (user) => {
    if (!user?.email) {
      setNotification({ message: 'Usuário sem e-mail para envio de alteração de senha.', type: 'warning' });
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Enviar Link de Alteração de Senha',
      message: `Deseja enviar o link de alteração de senha para ${user.name || user.email}?`,
      onConfirm: async () => {
        try {
          setPasswordResetTargetId(user.id || user.email);
          const { error } = await invokeAdminManageUser({
            action: 'send_password_reset',
            payload: {
              id: user.id || null,
              email: user.email,
              sas_partner_id: user.partnerId || user.sas_partner_id || null,
            },
          });
          if (error) {
            let reason = '';
            try {
              const ctx = await error.context?.json();
              const code = ctx?.error ? String(ctx.error) : '';
              const msg = ctx?.message ? String(ctx.message) : '';
              reason = (code && msg) ? `${code}: ${msg}` : (msg || code || ctx?.details || error.message || '');
            } catch { reason = error.message || ''; }
            throw new Error(reason || 'Falha desconhecida');
          }
          setNotification({ message: 'Link de alteração de senha enviado com sucesso.', type: 'success' });
          if (canViewUserLogs) await reloadLogs();
        } catch (err) {
          if (err?.code === 'SESSION_EXPIRED' || err?.code === 'INVALID_TOKEN' || err?.code === 'INVALID_SESSION') {
            setNotification({ message: 'Sessão expirada. Faça login novamente.', type: 'error' });
            return;
          }
          console.error('Send password reset link error:', err);
          setNotification({ message: `Falha ao enviar link de alteração de senha. ${err?.message ? String(err.message) : ''}`, type: 'error' });
        } finally {
          setPasswordResetTargetId(null);
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  const openSetPasswordModal = (user) => {
    if (!user?.id) {
      setNotification({ message: 'Usuário inválido para definir senha.', type: 'warning' });
      return;
    }
    if (auth?.session?.user?.id && String(auth.session.user.id) === String(user.id)) {
      setNotification({ message: 'Use o fluxo de perfil para alterar sua própria senha.', type: 'warning' });
      return;
    }
    setSetPasswordModal({
      isOpen: true,
      user,
      password: '',
      confirmPassword: '',
    });
  };

  const closeSetPasswordModal = () => {
    if (setPasswordSubmitting) return;
    setSetPasswordShow(false);
    setSetPasswordConfirmShow(false);
    setSetPasswordModal({
      isOpen: false,
      user: null,
      password: '',
      confirmPassword: '',
    });
  };

  const validateDirectPassword = (password, confirmPassword) => {
    const p = String(password || '');
    const c = String(confirmPassword || '');
    if (!p) return 'Informe a nova senha.';
    if (p.length < 8) return 'A senha deve ter pelo menos 8 caracteres.';
    if (!/[A-Z]/.test(p)) return 'A senha deve conter pelo menos 1 letra maiúscula.';
    if (!/[a-z]/.test(p)) return 'A senha deve conter pelo menos 1 letra minúscula.';
    if (!/[0-9]/.test(p)) return 'A senha deve conter pelo menos 1 número.';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(p)) return 'A senha deve conter pelo menos 1 caractere especial.';
    if (p !== c) return 'As senhas não conferem.';
    return null;
  };

  const humanizeSetPasswordError = (rawMessage) => {
    const raw = String(rawMessage || '').trim();
    const lower = raw.toLowerCase();
    if (!raw) return 'Falha ao definir senha. Tente novamente.';
    if (lower.includes('password is known to be weak') || lower.includes('easy to guess')) {
      return 'Senha recusada por ser fraca/comum. Use uma senha menos previsível.';
    }
    if (lower.includes('new password should be different') || lower.includes('password should be different')) {
      return 'A nova senha precisa ser diferente da senha atual.';
    }
    if (lower.includes('invalid action')) {
      return 'Ação de senha não disponível nesta versão do servidor. Tente novamente em instantes.';
    }
    if (lower.includes('access denied')) {
      return 'Você não tem permissão para alterar senha.';
    }
    return raw;
  };

  const setPasswordChecks = useMemo(() => {
    const password = String(setPasswordModal.password || '');
    const confirmPassword = String(setPasswordModal.confirmPassword || '');
    return [
      { key: 'length', label: 'Pelo menos 8 caracteres', met: password.length >= 8 },
      { key: 'uppercase', label: 'Pelo menos 1 letra maiúscula', met: /[A-Z]/.test(password) },
      { key: 'lowercase', label: 'Pelo menos 1 letra minúscula', met: /[a-z]/.test(password) },
      { key: 'number', label: 'Pelo menos 1 número', met: /[0-9]/.test(password) },
      { key: 'special', label: 'Pelo menos 1 caractere especial', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
      { key: 'match', label: 'As senhas coincidem', met: !!confirmPassword && password === confirmPassword },
    ];
  }, [setPasswordModal.password, setPasswordModal.confirmPassword]);

  const manualPasswordChecks = useMemo(() => {
    const password = String(manualForm.password || '');
    const confirmPassword = String(manualForm.confirmPassword || '');
    return [
      { key: 'length', label: 'Pelo menos 8 caracteres', met: password.length >= 8 },
      { key: 'uppercase', label: 'Pelo menos 1 letra maiúscula', met: /[A-Z]/.test(password) },
      { key: 'lowercase', label: 'Pelo menos 1 letra minúscula', met: /[a-z]/.test(password) },
      { key: 'number', label: 'Pelo menos 1 número', met: /[0-9]/.test(password) },
      { key: 'special', label: 'Pelo menos 1 caractere especial', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
      { key: 'match', label: 'As senhas coincidem', met: !!confirmPassword && password === confirmPassword },
    ];
  }, [manualForm.password, manualForm.confirmPassword]);

  const handleSetUserPassword = async () => {
    const user = setPasswordModal.user;
    if (!user?.id) return;

    const validationError = validateDirectPassword(setPasswordModal.password, setPasswordModal.confirmPassword);
    if (validationError) {
      setNotification({ message: validationError, type: 'warning' });
      return;
    }

    setSetPasswordSubmitting(true);
    setSetPasswordTargetId(user.id);
    try {
      let result = await invokeAdminManageUser({
        action: 'set_user_password',
        payload: {
          id: user.id,
          password: setPasswordModal.password,
        },
      });

      // Compatibilidade: se a Edge Function ainda não tiver a action nova em produção,
      // reutiliza o fluxo "update" existente para aplicar a senha.
      if (result?.error) {
        let fallbackAllowed = false;
        try {
          const ctx = await result.error.context?.json?.();
          const msg = String(ctx?.error || ctx?.message || result.error?.message || '').toLowerCase();
          fallbackAllowed = msg.includes('invalid action');
        } catch {
          const msg = String(result.error?.message || '').toLowerCase();
          fallbackAllowed = msg.includes('invalid action');
        }

        if (fallbackAllowed) {
          result = await invokeAdminManageUser({
            action: 'update',
            payload: {
              id: user.id,
              email: user.email || undefined,
              password: setPasswordModal.password,
              full_name: user.name || user.full_name || undefined,
              cpf: user.cpf || undefined,
              role: user.profile || user.role || undefined,
              status: user.status || undefined,
              company_id: user.companyId || user.company_id || undefined,
              sas_partner_id: user.partnerId || user.sas_partner_id || undefined,
              whatsapp: user.whatsapp || null,
            },
          });
        }
      }

      if (result?.error) {
        let reason = '';
        try {
          const ctx = await result.error.context?.json?.();
          const code = ctx?.error ? String(ctx.error) : '';
          const msg = ctx?.message ? String(ctx.message) : '';
          reason = (code && msg) ? `${code}: ${msg}` : (msg || code || ctx?.details || result.error.message || '');
        } catch {
          reason = result.error.message || '';
        }
        throw new Error(humanizeSetPasswordError(reason || result.error.message || ''));
      }

      setNotification({ message: 'Senha definida com sucesso pelo Admin.', type: 'success' });
      if (canViewUserLogs) await reloadLogs();
      closeSetPasswordModal();
    } catch (err) {
      if (err?.code === 'SESSION_EXPIRED' || err?.code === 'INVALID_TOKEN' || err?.code === 'INVALID_SESSION') {
        setNotification({ message: 'Sessão expirada. Faça login novamente.', type: 'error' });
        return;
      }
      console.error('Set user password error:', err);
      setNotification({ message: `Falha ao definir senha. ${err?.message ? String(err.message) : ''}`, type: 'error' });
    } finally {
      setSetPasswordSubmitting(false);
      setSetPasswordTargetId(null);
    }
  };

  const handleImpersonate = (user) => {
    const currentUserId = auth?.session?.user?.id || null;
    const accessToken = auth?.session?.access_token || null;
    if (!auth?.isAdmin) {
      setNotification({ message: 'Apenas Admin pode impersonar usuários.', type: 'error' });
      return;
    }
    if (!accessToken) {
      setNotification({ message: 'Sessão inválida. Faça login novamente.', type: 'error' });
      return;
    }
    if (!user?.id) {
      setNotification({ message: 'Usuário inválido.', type: 'error' });
      return;
    }
    if (currentUserId && String(user.id) === String(currentUserId)) {
      setNotification({ message: 'Você não pode impersonar sua própria conta.', type: 'error' });
      return;
    }
    if (String(user.profile || '').toLowerCase() === 'admin') {
      setNotification({ message: 'Você não pode impersonar outro Admin.', type: 'error' });
      return;
    }
    setConfirmModal({
      isOpen: true,
      title: 'Impersonar Usuário',
      message: `Você será desconectado do Admin e logado como ${user.name || user.email}. Deseja continuar?`,
      onConfirm: async () => {
        try {
          setImpersonatingUserId(user.id);
          const redirectTo = `${window.location.origin}/impersonation/callback`;
          const data = await invokeEdgeFunction('admin-impersonate', {
            body: { target_user_id: user.id, redirect_to: redirectTo, access_token: accessToken },
            accessToken
          })
          const nonce = data?.return_nonce || '';
          if (nonce) {
            try { sessionStorage.setItem('sofera:impersonation:return_nonce', nonce); } catch {}
          }
          // Enable strict impersonation mode so permissions reproduce target-user behavior.
          try { sessionStorage.setItem('sofera:impersonation:strict', '1'); } catch {}
          const otp = data?.email_otp || null;
          const email = data?.email || null;
          if (otp && email) {
            const { error: vErr } = await supabase.auth.verifyOtp({ type: 'magiclink', token: otp, email });
            if (vErr) throw vErr;
            return;
          }
          const link = data?.action_link || null;
          if (!link) throw new Error('Link de acesso não foi gerado.');
          window.location.href = link;
        } catch (err) {
          setNotification({ message: String(err?.message || err || 'Falha ao iniciar impersonação.'), type: 'error' });
          setImpersonatingUserId(null);
        } finally {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null });
        }
      }
    });
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in">
      {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      
      <ConfirmModal 
        isOpen={confirmModal.isOpen} 
        title={confirmModal.title} 
        message={confirmModal.message} 
        onConfirm={confirmModal.onConfirm} 
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })} 
      />

      <InfoModal 
        isOpen={infoModal.isOpen} 
        title={infoModal.title} 
        content={infoModal.content} 
        onClose={() => setInfoModal({ ...infoModal, isOpen: false })} 
      />

      {setPasswordModal.isOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Definir Nova Senha</h3>
              <button
                onClick={closeSetPasswordModal}
                disabled={setPasswordSubmitting}
                className="text-slate-500 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-slate-300 mb-4">
              Usuário: <strong className="text-white">{setPasswordModal.user?.name || setPasswordModal.user?.email}</strong>
            </p>

            <div className="space-y-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Nova senha</label>
                <div className="relative">
                  <input
                    type={setPasswordShow ? 'text' : 'password'}
                    value={setPasswordModal.password}
                    onChange={(e) => setSetPasswordModal((prev) => ({ ...prev, password: e.target.value }))}
                    className="w-full bg-slate-950 border border-slate-800 p-3 pr-12 rounded-lg text-white focus:border-orange-500 outline-none transition"
                    placeholder="Digite a nova senha"
                    disabled={setPasswordSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setSetPasswordShow((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    disabled={setPasswordSubmitting}
                    title={setPasswordShow ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {setPasswordShow ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Confirmar senha</label>
                <div className="relative">
                  <input
                    type={setPasswordConfirmShow ? 'text' : 'password'}
                    value={setPasswordModal.confirmPassword}
                    onChange={(e) => setSetPasswordModal((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full bg-slate-950 border border-slate-800 p-3 pr-12 rounded-lg text-white focus:border-orange-500 outline-none transition"
                    placeholder="Repita a nova senha"
                    disabled={setPasswordSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => setSetPasswordConfirmShow((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    disabled={setPasswordSubmitting}
                    title={setPasswordConfirmShow ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {setPasswordConfirmShow ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3 space-y-2">
                {setPasswordChecks.map((check) => (
                  <div key={check.key} className="flex items-center gap-2 text-xs">
                    {check.met ? (
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    )}
                    <span className={check.met ? 'text-green-300' : 'text-slate-400'}>
                      {check.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={closeSetPasswordModal}
                disabled={setPasswordSubmitting}
                className="flex-1 px-4 py-2 text-slate-300 bg-slate-800 rounded-lg font-bold hover:bg-slate-700 transition disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSetUserPassword}
                disabled={setPasswordSubmitting}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg font-bold hover:bg-orange-400 transition disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {setPasswordSubmitting ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
                Definir Senha
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkResendModal.isOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl shadow-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Reenvio Convite Massa</h3>
              <button onClick={closeBulkResendModal} className="text-slate-500 hover:text-white" disabled={bulkResendSubmitting}>
                <X size={20} />
              </button>
            </div>

            <div className="mb-3">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Data de Cadastro</label>
              <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Início</label>
                  <DarkDatePicker
                    id="bulk-resend-start-date"
                    value={bulkResendModal.startDate}
                    onChange={(startDate) => setBulkResendModal((prev) => ({
                      ...prev,
                      startDate,
                      endDate: prev.endDate && startDate && prev.endDate < startDate ? '' : prev.endDate,
                    }))}
                    disabled={bulkResendSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Fim</label>
                  <DarkDatePicker
                    id="bulk-resend-end-date"
                    value={bulkResendModal.endDate}
                    min={bulkResendModal.startDate || undefined}
                    onChange={(endDate) => setBulkResendModal((prev) => ({ ...prev, endDate }))}
                    disabled={bulkResendSubmitting}
                  />
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-400">
                Usuários no recorte: <span className="text-white font-semibold">{getBulkResendTargetUsers().length}</span>
              </p>
            </div>

            <div className="mb-3">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Status</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-orange-500"
                    checked={Boolean(bulkResendModal.statuses.pending)}
                    onChange={() => toggleBulkResendStatus('pending')}
                    disabled={bulkResendSubmitting}
                  />
                  <span>Pendente</span>
                </label>
                <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-orange-500"
                    checked={Boolean(bulkResendModal.statuses.active)}
                    onChange={() => toggleBulkResendStatus('active')}
                    disabled={bulkResendSubmitting}
                  />
                  <span>Ativo</span>
                </label>
              </div>
              <p className="mt-2 text-xs text-slate-400">
                O reenvio em massa mantém o processo de convite original para os status selecionados.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer min-w-0">
                <input
                  type="checkbox"
                  className="accent-orange-500"
                  checked={Boolean(bulkResendModal.channels.email)}
                  onChange={() => toggleBulkResendChannel('email')}
                  disabled={bulkResendSubmitting}
                />
                <span>E-mail</span>
              </label>
              <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer min-w-0">
                <input
                  type="checkbox"
                  className="accent-orange-500"
                  checked={Boolean(bulkResendModal.channels.sms)}
                  onChange={() => toggleBulkResendChannel('sms')}
                  disabled={bulkResendSubmitting}
                />
                <span>SMS</span>
              </label>
              <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer min-w-0">
                <input
                  type="checkbox"
                  className="accent-orange-500"
                  checked={Boolean(bulkResendModal.channels.whatsapp)}
                  onChange={() => toggleBulkResendChannel('whatsapp')}
                  disabled={bulkResendSubmitting}
                />
                <span>WhatsApp</span>
              </label>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={closeBulkResendModal}
                disabled={bulkResendSubmitting}
                className="flex-1 px-4 py-2 text-slate-300 bg-slate-800 rounded-lg font-bold hover:bg-slate-700 transition disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleBulkResendInvites}
                disabled={
                  bulkResendSubmitting ||
                  (!bulkResendModal.statuses.pending && !bulkResendModal.statuses.active) ||
                  (!bulkResendModal.channels.email && !bulkResendModal.channels.sms && !bulkResendModal.channels.whatsapp)
                }
                style={{ backgroundColor: partner.color }}
                className="flex-1 px-4 py-2 text-white rounded-lg font-bold shadow-lg hover:opacity-90 transition disabled:opacity-60"
              >
                {bulkResendSubmitting ? 'Reenviando...' : 'Reenviar Convite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedResendModal.isOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Reenviar Convite</h3>
              <button onClick={closeSelectedResendModal} className="text-slate-500 hover:text-white" disabled={selectedResendSubmitting}>
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              {isEmailOnlyInviteRole
                ? <>O reenvio para seu perfil ocorre somente por e-mail para os <span className="text-white font-semibold">{getSelectedUsers().filter((u) => canScopedUserResendInvite(u)).length}</span> usuário(s) selecionado(s) elegível(eis).</>
                : <>Escolha os canais para reenviar o convite para os <span className="text-white font-semibold">{getSelectedUsers().filter((u) => canScopedUserResendInvite(u)).length}</span> usuário(s) selecionado(s) elegível(eis).</>}
            </p>
            <div className="rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2.5 mb-4 flex items-start gap-2 text-[11px] text-slate-400">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-orange-400" />
              <div className="leading-snug">
                <div className="font-semibold text-slate-200">Observações</div>
                <div>• Por padrão, somente o canal <span className="text-white font-semibold">E-mail</span> vem marcado.</div>
                <div>• Usuários sem <span className="text-white">e-mail</span> são ignorados.</div>
                {!isEmailOnlyInviteRole && (
                  <>
                    <div>• Usuários sem WhatsApp ou DDD 11/Número são ignorados para WhatsApp/SMS.</div>
                    <div>• Ponto Focal e Parceiro SAS enviam somente por E-mail.</div>
                  </>
                )}
                <div>• Total de usuários selecionados: <span className="text-white font-semibold">{selectedUserIdsSet.size}</span>.</div>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-orange-500"
                  checked={Boolean(selectedResendModal.channels.email)}
                  onChange={() => toggleSelectedResendChannel('email')}
                  disabled={selectedResendSubmitting}
                />
                <span>E-mail</span>
              </label>
              <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-orange-500"
                  checked={Boolean(selectedResendModal.channels.sms)}
                  onChange={() => toggleSelectedResendChannel('sms')}
                  disabled={selectedResendSubmitting || isEmailOnlyInviteRole}
                />
                <span>SMS</span>
              </label>
              <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-orange-500"
                  checked={Boolean(selectedResendModal.channels.whatsapp)}
                  onChange={() => toggleSelectedResendChannel('whatsapp')}
                  disabled={selectedResendSubmitting || isEmailOnlyInviteRole}
                />
                <span>WhatsApp</span>
              </label>
            </div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={closeSelectedResendModal}
                disabled={selectedResendSubmitting}
                className="flex-1 px-4 py-2 text-slate-300 bg-slate-800 rounded-lg font-bold hover:bg-slate-700 transition disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSelectedResendInvites}
                disabled={
                  selectedResendSubmitting ||
                  (!selectedResendModal.channels.email && !selectedResendModal.channels.sms && !selectedResendModal.channels.whatsapp)
                }
                style={{ backgroundColor: partner.color }}
                className="flex-1 px-4 py-2 text-white rounded-lg font-bold shadow-lg hover:opacity-90 transition disabled:opacity-60"
              >
                {selectedResendSubmitting ? 'Reenviando...' : 'Reenviar Convite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkPasswordResetModal.isOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Enviar Link de Alteração de Senha</h3>
              <button onClick={closeBulkPasswordResetModal} className="text-slate-500 hover:text-white" disabled={bulkPasswordResetSubmitting}>
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Esta ação envia um link de alteração de senha por e-mail para cada usuário selecionado que possua e-mail cadastrado.
            </p>
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 mb-4 flex items-start gap-2 text-[11px] text-amber-200">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-amber-300" />
              <div className="leading-snug">
                <div>• Total de usuários selecionados: <span className="text-white font-semibold">{selectedUserIdsSet.size}</span></div>
                <div>• Os que não possuírem e-mail válido serão pulados automaticamente.</div>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={closeBulkPasswordResetModal}
                disabled={bulkPasswordResetSubmitting}
                className="flex-1 px-4 py-2 text-slate-300 bg-slate-800 rounded-lg font-bold hover:bg-slate-700 transition disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleBulkPasswordReset}
                disabled={bulkPasswordResetSubmitting}
                style={{ backgroundColor: partner.color }}
                className="flex-1 px-4 py-2 text-white rounded-lg font-bold shadow-lg hover:opacity-90 transition disabled:opacity-60"
              >
                {bulkPasswordResetSubmitting ? 'Enviando...' : 'Enviar Links'}
              </button>
            </div>
          </div>
        </div>
      )}

      {bulkDeleteModal.isOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Excluir usuários em massa</h3>
              <button onClick={closeBulkDeleteModal} className="text-slate-500 hover:text-white" disabled={bulkDeleteSubmitting}>
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              Tem certeza que deseja excluir os usuários selecionados? Esta ação é irreversível.
            </p>
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2.5 mb-4 flex items-start gap-2 text-[11px] text-rose-200">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-rose-300" />
              <div className="leading-snug">
                <div>• Total selecionado: <span className="text-white font-semibold">{selectedUserIdsSet.size}</span></div>
                <div>• Sua própria conta será automaticamente preservada e não será excluída.</div>
                <div>• Matrículas, certificados e registros associados podem ser impactados.</div>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={closeBulkDeleteModal}
                disabled={bulkDeleteSubmitting}
                className="flex-1 px-4 py-2 text-slate-300 bg-slate-800 rounded-lg font-bold hover:bg-slate-700 transition disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={bulkDeleteSubmitting}
                className="flex-1 px-4 py-2 text-white bg-rose-600 hover:bg-rose-500 rounded-lg font-bold shadow-lg hover:opacity-90 transition disabled:opacity-60"
              >
                {bulkDeleteSubmitting ? 'Excluindo...' : 'Confirmar Exclusão'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resendInviteModal.isOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Reenviar Convite</h3>
              <button onClick={closeResendInviteModal} className="text-slate-500 hover:text-white" disabled={resendSubmitting}>
                <X size={20} />
              </button>
            </div>
            <p className="text-sm text-slate-400 mb-4">
              {isEmailOnlyInviteRole
                ? <>O reenvio para seu perfil ocorre somente por e-mail para <span className="text-white font-semibold">{resendInviteModal.user?.name || resendInviteModal.user?.email || 'usuário'}</span>.</>
                : <>Escolha os canais para reenviar o convite para <span className="text-white font-semibold">{resendInviteModal.user?.name || resendInviteModal.user?.email || 'usuário'}</span>.</>}
            </p>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-orange-500"
                  checked={Boolean(resendInviteModal.channels.email)}
                  onChange={() => toggleResendChannel('email')}
                  disabled={resendSubmitting || isEmailOnlyInviteRole}
                />
                <span>E-mail</span>
              </label>
              {!isEmailOnlyInviteRole && (
                <>
              <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-orange-500"
                  checked={Boolean(resendInviteModal.channels.sms)}
                  onChange={() => toggleResendChannel('sms')}
                  disabled={resendSubmitting}
                />
                <span>SMS</span>
              </label>
              <label className="flex items-center gap-3 text-sm text-slate-200 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-orange-500"
                  checked={Boolean(resendInviteModal.channels.whatsapp)}
                  onChange={() => toggleResendChannel('whatsapp')}
                  disabled={resendSubmitting}
                />
                <span>WhatsApp</span>
              </label>
                </>
              )}
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={closeResendInviteModal}
                disabled={resendSubmitting}
                className="flex-1 px-4 py-2 text-slate-300 bg-slate-800 rounded-lg font-bold hover:bg-slate-700 transition disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleResendInvite}
                disabled={resendSubmitting || (!resendInviteModal.channels.email && !resendInviteModal.channels.sms && !resendInviteModal.channels.whatsapp)}
                style={{ backgroundColor: partner.color }}
                className="flex-1 px-4 py-2 text-white rounded-lg font-bold shadow-lg hover:opacity-90 transition disabled:opacity-60"
              >
                {resendSubmitting ? 'Reenviando...' : 'Reenviar Convite'}
              </button>
            </div>
          </div>
        </div>
      )}

      <BulkCreateUserModal 
        isOpen={showMass}
        onClose={() => setShowMass(false)}
        onSuccess={async ({ successCount = 0, failedCount = 0, createdOnlyCount = 0, enrollmentWarningCount = 0, inviteWarningCount = 0, autoEnrollEnabled = false, courseSummaries = [] } = {}) => {
          await reloadUsers();
          if (canViewUserLogs) await reloadLogs();
          const totalEnrollmentsCreated = (courseSummaries || []).reduce((acc, course) => acc + (Number(course?.enrollmentCount) || 0), 0);
          const zeroEnrollmentsWithAutoEnroll = autoEnrollEnabled && (successCount + createdOnlyCount) > 0 && totalEnrollmentsCreated === 0;

          if (failedCount > 0) {
            setNotification({
              message: autoEnrollEnabled
                ? `Lote concluído com pendências: ${successCount} usuário(s) cadastrado(s) e matriculado(s), ${createdOnlyCount} cadastrado(s) sem matrícula concluída e ${Math.max(failedCount - createdOnlyCount, 0)} falha(s) de cadastro.`
                : `${successCount} usuário(s) processado(s) com sucesso e ${failedCount} falha(s) no lote.`,
              type: 'warning'
            });
            return;
          }

          if (autoEnrollEnabled) {
            setNotification({
              message: zeroEnrollmentsWithAutoEnroll
                ? `Lote finalizado sem matrículas efetivadas. ${successCount + createdOnlyCount} usuário(s) foram criados, mas nenhuma matrícula foi criada.`
                : inviteWarningCount > 0
                  ? `${successCount} usuário(s) cadastrado(s)/matriculado(s), mas ${inviteWarningCount} solicitação(ões) de convite ao Supabase falharam.`
                : enrollmentWarningCount > 0
                ? `${successCount} usuário(s) cadastrado(s), mas ${enrollmentWarningCount} matrícula(s) exigem revisão.`
                : `${successCount} usuário(s) cadastrado(s) e matriculado(s) com sucesso!`,
              type: (enrollmentWarningCount > 0 || zeroEnrollmentsWithAutoEnroll || inviteWarningCount > 0) ? 'warning' : 'success'
            });
            return;
          }

          setNotification({ message: `${successCount} usuário(s) cadastrado(s) com sucesso!`, type: 'success' });
        }}
        partners={partners}
        companies={companies}
      />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Gestão de Usuários</h1>
          <p className="text-slate-400">{(effectiveRole === 'Ponto Focal' || isPartnerScopedRole(effectiveRole)) ? 'Acompanhamento de usuários e acessos sob sua gestão.' : 'Controle de acessos de alunos e coordenadores.'}</p>
        </div>
        {activeTab === 'geral' && (
          <div className="flex gap-3">
            <button
              onClick={handleExportUsersExcel}
              disabled={loading || visibleUsers.length === 0}
              className="bg-slate-800 text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 border border-slate-700 hover:bg-slate-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              title="Exportar Excel com os filtros aplicados"
            >
              <FileSpreadsheet size={18} /> Exportar Excel
            </button>
            {canCreateUsers && (
              <>
                <button onClick={() => setShowMass(true)} className="bg-slate-800 text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 border border-slate-700 hover:bg-slate-700 transition">
                  <FileUp size={18} /> Criar em Massa
                </button>
                <button onClick={openBulkResendModal} className="bg-slate-800 text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 border border-slate-700 hover:bg-slate-700 transition">
                  <Shield size={18} /> Reenvio Convite Massa
                </button>
                <button 
                  onClick={() => { resetForm(); setShowManual(true); }} 
                  style={{ backgroundColor: partner.color }} 
                  className="text-white px-5 py-2.5 rounded-lg font-bold flex items-center gap-2 shadow-lg hover:opacity-90 transition"
                >
                  <UserPlus size={18} /> Novo Usuário
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {activeTab === 'geral' && (
        <div className="mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {summaryCards.map((item) => (
              <SummaryCard
                key={item.key}
                label={item.label}
                value={loading ? '...' : item.value}
                accentClass={item.accentClass}
                testId={item.testId}
              />
            ))}
          </div>
        </div>
      )}

      <div className="border-b border-slate-800 mb-6 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          <button
            type="button"
            onClick={() => setActiveTab('geral')}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-wide transition border-b-2 ${
              activeTab === 'geral' ? 'text-teal-400 border-teal-400' : 'text-slate-500 border-transparent hover:text-slate-300'
            }`}
            aria-current={activeTab === 'geral' ? 'page' : undefined}
          >
            Geral
          </button>
          {canViewUserLogs && (
            <button
              type="button"
              onClick={() => setActiveTab('logs')}
              className={`px-4 py-3 text-sm font-bold uppercase tracking-wide transition border-b-2 ${
                activeTab === 'logs' ? 'text-teal-400 border-teal-400' : 'text-slate-500 border-transparent hover:text-slate-300'
              }`}
              aria-current={activeTab === 'logs' ? 'page' : undefined}
            >
              Logs
            </button>
          )}
          {canManageRoleDefinitions && (
          <button
            type="button"
            onClick={() => setActiveTab('perfis')}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-wide transition border-b-2 ${
              activeTab === 'perfis' ? 'text-teal-400 border-teal-400' : 'text-slate-500 border-transparent hover:text-slate-300'
            }`}
            aria-current={activeTab === 'perfis' ? 'page' : undefined}
          >
            Perfis
          </button>
          )}
          <button
            type="button"
            onClick={() => setActiveTab('base-core')}
            className={`px-4 py-3 text-sm font-bold uppercase tracking-wide transition border-b-2 ${
              activeTab === 'base-core' ? 'text-teal-400 border-teal-400' : 'text-slate-500 border-transparent hover:text-slate-300'
            }`}
            aria-current={activeTab === 'base-core' ? 'page' : undefined}
          >
            Base Core
          </button>
        </div>
      </div>

      {activeTab === 'geral' && (
      <div className={`bg-slate-900 border border-slate-800 rounded-xl p-6 mb-6 grid grid-cols-1 gap-6 ${effectiveRole === 'Admin' ? 'lg:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-2'}`}>
        <div className="flex flex-col gap-2">
          <label className="text-[11px] uppercase font-bold tracking-[0.18em] text-slate-500">Nome/E-mail</label>
          <div className="relative">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Pesquisar..."
              className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-12 pr-4 py-3 text-sm text-white outline-none focus:border-slate-600 focus:ring-1 focus:ring-primary/40 transition min-h-[48px] hover:border-slate-700 placeholder:text-slate-600"
            />
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          </div>
        </div>
        {companyOptions.length > 0 && (
          <div className="flex flex-col gap-2">
            <label className="text-[11px] uppercase font-bold tracking-[0.18em] text-slate-500">Empresa(s)</label>
            <MultiSelect options={companyOptions} value={companyFilter} onChange={setCompanyFilter} placeholder="Selecionar empresas" allLabel="Todas as Empresas" />
            {companyFilter.length > 0 && <span className="text-[11px] text-slate-500 font-medium">{companyFilter.length} selecionada(s)</span>}
          </div>
        )}
        {effectiveRole === 'Admin' && (
          <div className="flex flex-col gap-2">
            <label className="text-[11px] uppercase font-bold tracking-[0.18em] text-slate-500">Parceiro SAS</label>
            <MultiSelect options={partnerOptions} value={partnerFilter} onChange={setPartnerFilter} placeholder="Selecionar parceiros" allLabel="Todos os Parceiros" />
            {partnerFilter.length > 0 && <span className="text-[11px] text-slate-500 font-medium">{partnerFilter.length} selecionado(s)</span>}
          </div>
        )}
        <div className="flex flex-col gap-2">
          <label className="text-[11px] uppercase font-bold tracking-[0.18em] text-slate-500">Perfil</label>
          <MultiSelect
            options={roleOptions}
            value={roleFilter}
            onChange={setRoleFilter}
            placeholder="Todos os Perfis"
            allLabel="Todos os Perfis"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-[11px] uppercase font-bold tracking-[0.18em] text-slate-500">Status</label>
          <MultiSelect
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Todos os Status"
            allLabel="Todos os Status"
          />
        </div>
        <div className={`${effectiveRole === 'Admin' ? 'lg:col-span-3' : 'sm:col-span-2'} flex justify-end items-end`}>
          <button
            type="button"
            onClick={clearUsersFilters}
            className="px-5 py-3 rounded-lg border border-slate-700 bg-slate-950 text-sm font-bold uppercase tracking-wide text-slate-300 hover:bg-slate-800 hover:text-white transition min-h-[48px]"
          >
            Limpar filtros
          </button>
        </div>
      </div>
      )}

      {activeTab === 'geral' && (
      <>
      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto">
        {selectedUserIdsSet.size > 0 && (
          <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 bg-orange-500/10 border-b border-orange-500/30 px-5 py-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-orange-500 flex items-center justify-center text-white text-xs font-bold">
                {selectedUserIdsSet.size}
              </div>
              <span className="text-sm font-bold text-orange-200">
                {selectedUserIdsSet.size === 1 ? 'usuário selecionado' : 'usuários selecionados'}
              </span>
            </div>
            <div className="flex-1 flex items-center justify-end gap-2 flex-wrap">
              <div className="relative inline-flex">
                <BulkActionsDropdown
                  selectedCount={selectedUserIdsSet.size}
                  canUseAdminOnlyActions={canUseAdminOnlyActions}
                  onResendInvite={() => setSelectedResendModal({ isOpen: true, channels: { email: true, sms: false, whatsapp: false } })}
                  onSendPasswordReset={() => setBulkPasswordResetModal({ isOpen: true })}
                  onDelete={() => setBulkDeleteModal({ isOpen: true })}
                />
              </div>
              <button
                type="button"
                onClick={clearUserSelection}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-orange-500/30 bg-slate-900/80 text-xs font-bold text-orange-300 hover:text-white hover:bg-orange-500/20 hover:border-orange-500/50 transition"
              >
                <X size={14} />
                Limpar seleção
              </button>
            </div>
          </div>
        )}
        <table className="w-full text-left">
          <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold">
            <tr>
              <th className="p-5 w-[52px]">
                <label className="flex items-center justify-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="accent-orange-500 w-4 h-4 rounded"
                    checked={allUsersSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someUsersSelected;
                    }}
                    onChange={toggleSelectAllUsers}
                    disabled={loading || visibleUsers.length === 0}
                    title={allUsersSelected ? 'Desmarcar todos visíveis' : someUsersSelected ? 'Marcar todos visíveis' : 'Selecionar todos visíveis'}
                  />
                </label>
              </th>
              <th className="p-5">
                <div className="flex items-center gap-2">
                  <span>Nome</span>
                  <button
                    type="button"
                    onClick={() => toggleSort('name', 'asc')}
                    className={getSortButtonClassName('name')}
                    title={getSortButtonTitle('name')}
                  >
                    <ArrowUpDown size={12} />
                    {sortConfig?.field === 'name' ? getSortButtonLabel('name') : 'A/Z'}
                  </button>
                </div>
              </th>
              <th className="p-5">
                <div className="flex items-center gap-2">
                  <span>Empresa</span>
                  <button
                    type="button"
                    onClick={() => toggleSort('company', 'asc')}
                    className={getSortButtonClassName('company')}
                    title={getSortButtonTitle('company')}
                  >
                    <ArrowUpDown size={12} />
                    {sortConfig?.field === 'company' ? getSortButtonLabel('company') : 'A/Z'}
                  </button>
                </div>
              </th>
              <th className="p-5">Cargo</th>
              <th className="p-5">Área</th>
              <th className="p-5">Perfil</th>
              <th className="p-5">
                <div className="flex items-center gap-2">
                  <span>Status</span>
                  <button
                    type="button"
                    onClick={() => toggleSort('activation', 'desc')}
                    className={getSortButtonClassName('activation')}
                    title={getSortButtonTitle('activation')}
                  >
                    <ArrowUpDown size={12} />
                    {sortConfig?.field === 'activation' ? getSortButtonLabel('activation') : 'Recentes'}
                  </button>
                </div>
              </th>
              {canManageScopedUsers && <th className="p-5 text-right">Ações</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {loading && (
              <tr>
                <td colSpan={canManageScopedUsers ? 8 : 7} className="p-8 text-center text-slate-500">Carregando usuários...</td>
              </tr>
            )}
            {!loading && paginatedUsers.map(u => {
              const isSelected = selectedUserIdsSet.has(String(u.id || ''));
              return (
              <tr key={u.id} className={`transition text-slate-300 ${isSelected ? 'bg-orange-500/10 hover:bg-orange-500/15 border-l-2 border-l-orange-500' : 'hover:bg-slate-800/50 border-l-2 border-l-transparent'}`}>
                <td className="p-5 w-[52px]">
                  <label className="flex items-center justify-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-orange-500 w-4 h-4 rounded"
                      checked={isSelected}
                      onChange={() => toggleSelectOneUser(u.id)}
                    />
                  </label>
                </td>
                <td className="p-5">
                  <div className="font-bold text-white">{u.name}</div>
                  <div className="text-xs opacity-50">{u.email}</div>
                </td>
                <td className="p-5">{u.company}</td>
                <td className="p-5">{u.cargo || '—'}</td>
                <td className="p-5">{u.area || '—'}</td>
                <td className="p-5">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-white/5 text-slate-400 border border-white/5 uppercase">
                    {u.profile}
                  </span>
                </td>
                <td className="p-5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(() => {
                      const hasScheduledDispatchIssue = (
                        String(u.status || '').trim().toLowerCase() === 'programado'
                        && !u.last_invite_sent_at
                        && !!u.scheduled_invite_last_error
                      );
                      const isRetryScheduled = (
                        hasScheduledDispatchIssue
                        && String(u.scheduled_invite_job_status || '').toLowerCase() === 'pending'
                        && !!u.scheduled_invite_for
                      );
                      return (
                        <>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        u.status === 'Ativo'
                          ? 'bg-teal-400'
                          : u.status === 'Programado'
                            ? 'bg-amber-400'
                            : 'bg-rose-400'
                      }`}
                    ></div>
                    {u.status || '-'}
                          {hasScheduledDispatchIssue && (
                            <span className="inline-flex items-center rounded-full border border-rose-500/40 bg-rose-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-300">
                              {isRetryScheduled ? 'Reenvio agendado' : 'Falha no envio'}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  {(() => {
                    const statusDateMeta = getStatusDateMeta(u);
                    if (!statusDateMeta?.length) return null;
                    return (
                      <div className="mt-1 space-y-0.5 text-[11px] text-slate-400">
                        {statusDateMeta.map((item) => (
                          <div key={item.label}>
                            {item.label}: <span className="text-slate-300">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                {canManageScopedUsers && (
                  <td className="p-5 text-right">
                    <div className="flex justify-end">
                      <UserActionsDropdown
                        user={u}
                        canUseAdminOnlyActions={canUseAdminOnlyActions}
                        canScopedUserResendInvite={canScopedUserResendInvite}
                        auth={auth}
                        passwordResetTargetId={passwordResetTargetId}
                        setPasswordTargetId={setPasswordTargetId}
                        impersonatingUserId={impersonatingUserId}
                        onEdit={handleEdit}
                        onResendInvite={openResendInviteModal}
                        onSendPasswordReset={handleSendPasswordReset}
                        onSetPassword={openSetPasswordModal}
                        onImpersonate={handleImpersonate}
                        onDelete={handleDelete}
                      />
                    </div>
                  </td>
                )}
              </tr>
            ); })}
            {!loading && visibleUsers.length === 0 && (
              <tr>
                <td colSpan={canManageScopedUsers ? 8 : 7} className="p-8 text-center text-slate-500">Nenhum usuário encontrado.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <DataTablePagination
        totalItems={visibleUsers.length}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onItemsPerPageChange={setItemsPerPage}
        onPageChange={setCurrentPage}
        activePageClass="bg-primary text-white"
        selectFocusClass="focus:border-primary"
      />
      </>
      )}

      {activeTab === 'logs' && canViewUserLogs && (
        <div className="space-y-4">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col md:flex-row gap-3 md:items-end md:justify-between">
            <div className="flex-1 max-w-2xl">
              <label className="text-[10px] uppercase font-bold text-slate-500">Buscar nos logs</label>
              <div className="relative mt-1">
                <input
                  type="text"
                  value={logsSearchInput}
                  onChange={(e) => setLogsSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setLogsSearch(logsSearchInput);
                  }}
                  placeholder="Nome, e-mail, CPF, WhatsApp, ação..."
                  className="w-full bg-slate-950 border border-slate-700 rounded pl-9 pr-3 py-2 text-xs text-white outline-none focus:border-orange-500 placeholder:text-slate-600"
                />
                <Search size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setLogsSearch(logsSearchInput)}
                className="px-4 py-2 rounded border border-slate-700 bg-slate-950 text-xs font-bold uppercase tracking-wide text-slate-300 hover:bg-slate-800 hover:text-white transition"
              >
                Buscar
              </button>
              <button
                type="button"
                onClick={() => reloadLogs()}
                className="px-4 py-2 rounded border border-slate-700 bg-slate-950 text-xs font-bold uppercase tracking-wide text-slate-300 hover:bg-slate-800 hover:text-white transition"
              >
                Atualizar
              </button>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-950/80 text-slate-400 text-sm uppercase font-semibold">
                <tr>
                  <th className="p-5">Data</th>
                  <th className="p-5">Ação</th>
                  <th className="p-5">Usuário Logado</th>
                  <th className="p-5">Usuário Alterado</th>
                  <th className="p-5">E-mail</th>
                  <th className="p-5">CPF</th>
                  <th className="p-5">WhatsApp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {logsLoading && (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-slate-500">Carregando logs...</td>
                  </tr>
                )}
                {!logsLoading && userLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-800/50 transition text-slate-300">
                    <td className="p-5 text-sm">{formatDateTimeLabel(log.created_at)}</td>
                    <td className="p-5">
                      {(() => {
                        const actionDetails = getUserLogActionDetails(log.action, log.meta);
                        return (
                          <div className="space-y-1">
                            <div className="inline-flex items-center gap-2 rounded-full border border-teal-500/20 bg-teal-500/10 px-3 py-1 text-xs font-bold text-teal-300">
                              <ScrollText size={12} />
                              {actionDetails.baseLabel}
                            </div>
                            {actionDetails.changedFields.length > 0 && (
                              <div className="text-xs text-slate-400">
                                Campos: <span className="text-slate-300">{actionDetails.changedFields.join(', ')}</span>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-5 text-sm">{log.requester_email || 'Não registrado'}</td>
                    <td className="p-5">
                      <div className="font-bold text-white">{log.target_name || 'Não registrado'}</div>
                    </td>
                    <td className="p-5 text-sm">{log.target_email || 'Não registrado'}</td>
                    <td className="p-5 text-sm">{log.target_cpf || 'Não registrado'}</td>
                    <td className="p-5 text-sm">{log.target_whatsapp || 'Não registrado'}</td>
                  </tr>
                ))}
                {!logsLoading && userLogs.length === 0 && (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-slate-500">Nenhum log encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'perfis' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-xl">
          <div className="p-5 border-b border-slate-800 flex items-center justify-between gap-4">
            <div>
              <div className="text-white font-bold text-lg">Perfis e Permissões</div>
              <div className="text-slate-400 text-sm">Edite as permissões por perfil</div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setShowManageRolesModal(true)}
                className="px-4 py-2.5 rounded-lg font-bold text-white bg-slate-800 border border-slate-700 hover:bg-slate-700 uppercase"
              >
                Editar/Excluir
              </button>
              <button
                type="button"
                onClick={openCreateRole}
                className="px-4 py-2.5 rounded-lg font-bold text-white bg-slate-800 border border-slate-700 hover:bg-slate-700"
              >
                + Perfil
              </button>
              <button
                type="button"
                onClick={saveMatrix}
                disabled={!rolesMatrix || savingMatrix}
                style={{ backgroundColor: partner.color }}
                className="px-5 py-2.5 rounded-lg text-white font-bold shadow-lg disabled:opacity-60"
              >
                {savingMatrix ? 'Salvando…' : 'Salvar Permissões'}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-950/80 text-slate-400 text-xs md:text-sm uppercase font-semibold">
                <tr>
                  <th className="p-4 sticky left-0 z-20 bg-slate-950/80">Perfil</th>
                  {PERMISSION_DEFS.map((p) => (
                    <th key={p.key} className="p-4 whitespace-nowrap">{p.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {rolesList.map((role) => (
                  <tr key={role} className="text-slate-300">
                    <td className="p-4 text-white font-medium whitespace-nowrap sticky left-0 z-10 bg-slate-900 border-r border-slate-800 flex items-center gap-3">
                      <span>{role}</span>
                    </td>
                    {PERMISSION_DEFS.map((p) => {
                      const val = rolesMatrix?.[role]?.[p.key] || false;
                      return (
                        <td key={p.key} className="p-4">
                          <button
                            type="button"
                            onClick={() => toggleRolePerm(role, p.key)}
                            className={`w-6 h-6 rounded-md border flex items-center justify-center transition ${val ? 'border-teal-500/40 bg-teal-500/10 text-teal-400' : 'border-slate-700 bg-slate-800 text-slate-500'} hover:border-slate-600`}
                            aria-pressed={val ? 'true' : 'false'}
                            aria-label={`${role} - ${p.label}`}
                          >
                            {val ? <Check size={16} /> : <X size={16} />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'base-core' && (
        <CoreBaseTab entities={['people', 'globalRoles']} />
      )}

      {showManageRolesModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Gerenciar Perfis</h3>
              <button onClick={()=>setShowManageRolesModal(false)} className="text-slate-500 hover:text-white"><X size={20}/></button>
            </div>
            <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
              {rolesList.map((role) => (
                <div key={role} className="flex items-center justify-between p-3 bg-slate-950 rounded-lg border border-slate-800">
                  <span className="text-white font-medium">{role}</span>
                  <div className="flex items-center gap-2">
                      <button 
                        onClick={() => { setShowManageRolesModal(false); openEditRole(role); }} 
                        className="p-2 text-slate-400 hover:text-white bg-slate-900 hover:bg-slate-800 rounded border border-slate-800 transition"
                        title="Editar Nome"
                      >
                        <Edit2 size={16}/>
                      </button>
                      <button 
                        onClick={() => confirmDeleteRole(role)} 
                        className="p-2 text-rose-500 hover:text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 rounded border border-rose-500/20 transition"
                        title="Excluir Perfil"
                      >
                        <Trash2 size={16}/>
                      </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={()=>setShowManageRolesModal(false)} className="px-4 py-2 bg-slate-800 text-white rounded-lg font-bold hover:bg-slate-700">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {showRoleModal && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">{editingRole ? 'Editar Perfil' : 'Novo Perfil'}</h3>
              <button onClick={()=>setShowRoleModal(false)} className="text-slate-500 hover:text-white"><X size={20}/></button>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Nome do Perfil</label>
              <input value={roleNameInput} onChange={(e)=>setRoleNameInput(e.target.value)} className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none focus:border-white transition" placeholder="Ex: Supervisor"/>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={()=>setShowRoleModal(false)} className="px-4 py-2 text-slate-400 font-bold hover:text-white transition">Cancelar</button>
              <button onClick={persistRole} style={{ backgroundColor: partner.color }} className="px-6 py-2 rounded-lg text-white font-bold shadow-lg">Salvar</button>
            </div>
          </div>
        </div>
      )}

      {showManual && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-slate-800 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">{editingId ? 'Editar Usuário' : 'Novo Usuário'}</h3>
              <X className="text-slate-500 cursor-pointer hover:text-white" onClick={closeManualModal} />
            </div>
            
            <form onSubmit={handleManualSubmit} className="p-6 flex flex-col gap-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                {/* Nome Completo - 6 cols */}
                <div className="md:col-span-6 flex flex-col gap-1.5">
                  <label htmlFor="full_name" className="text-xs font-bold text-slate-500 uppercase tracking-widest">Nome Completo</label>
                  <input 
                    id="full_name"
                    type="text" 
                    value={manualForm.name} 
                    onChange={e => setManualForm({...manualForm, name: e.target.value})} 
                    className={`bg-slate-950 border ${errors.name ? 'border-rose-500' : 'border-slate-800'} p-3 rounded-lg text-white focus:border-white outline-none transition`} 
                  />
                  {errors.name && <span className="text-rose-500 text-[10px] font-bold uppercase">{errors.name}</span>}
                </div>

                {/* E-mail - 6 cols */}
                <div className="md:col-span-6 flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-xs font-bold text-slate-500 uppercase tracking-widest">E-mail</label>
                  <input 
                    id="email"
                    type="email" 
                    value={manualForm.email} 
                    onChange={e => setManualForm({...manualForm, email: e.target.value})} 
                    className={`bg-slate-950 border ${errors.email ? 'border-rose-500' : 'border-slate-800'} p-3 rounded-lg text-white focus:border-white outline-none transition`} 
                  />
                  {errors.email && <span className="text-rose-500 text-[10px] font-bold uppercase">{errors.email}</span>}
                </div>

                {/* Password Section - Only for edição (opcional) */}
                {editingId && (
                  <>
                    {!isScopedUserManager && (
                    <div className="md:col-span-6 flex flex-col gap-1.5">
                      <label htmlFor="password" className="text-xs font-bold text-slate-500 uppercase tracking-widest">Senha</label>
                      <div className="relative">
                        <input 
                          id="password"
                          type={manualPasswordShow ? 'text' : 'password'} 
                          value={manualForm.password} 
                          onChange={e => setManualForm({...manualForm, password: e.target.value})} 
                          className={`w-full bg-slate-950 border ${errors.password ? 'border-rose-500' : 'border-slate-800'} p-3 pr-12 rounded-lg text-white focus:border-white outline-none transition`} 
                        />
                        <button
                          type="button"
                          onClick={() => setManualPasswordShow((prev) => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                          title={manualPasswordShow ? 'Ocultar senha' : 'Mostrar senha'}
                        >
                          {manualPasswordShow ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.password && <span className="text-rose-500 text-[10px] font-bold uppercase">{errors.password}</span>}
                    </div>
                    )}
                    {!isScopedUserManager && (
                    <div className="md:col-span-6 flex flex-col gap-1.5">
                      <label htmlFor="confirmPassword" className="text-xs font-bold text-slate-500 uppercase tracking-widest">Confirmar Senha</label>
                      <div className="relative">
                        <input 
                          id="confirmPassword"
                          type={manualPasswordConfirmShow ? 'text' : 'password'} 
                          value={manualForm.confirmPassword} 
                          onChange={e => setManualForm({...manualForm, confirmPassword: e.target.value})} 
                          className={`w-full bg-slate-950 border ${errors.confirmPassword ? 'border-rose-500' : 'border-slate-800'} p-3 pr-12 rounded-lg text-white focus:border-white outline-none transition`} 
                        />
                        <button
                          type="button"
                          onClick={() => setManualPasswordConfirmShow((prev) => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                          title={manualPasswordConfirmShow ? 'Ocultar senha' : 'Mostrar senha'}
                        >
                          {manualPasswordConfirmShow ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {errors.confirmPassword && <span className="text-rose-500 text-[10px] font-bold uppercase">{errors.confirmPassword}</span>}
                    </div>
                    )}
                    {!isScopedUserManager && (manualForm.password || manualForm.confirmPassword) && (
                      <div className="md:col-span-12 rounded-xl border border-slate-800 bg-slate-950/60 p-3 space-y-2">
                        {manualPasswordChecks.map((check) => (
                          <div key={check.key} className="flex items-center gap-2 text-xs">
                            {check.met ? (
                              <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0" />
                            )}
                            <span className={check.met ? 'text-green-300' : 'text-slate-400'}>
                              {check.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Empresa principal - 4 cols */}
                <div className="md:col-span-4 flex flex-col gap-1.5">
                  <label htmlFor="companyId" className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                    {manualForm.profile === 'Ponto Focal' ? 'Empresa Principal' : 'Empresa'}
                  </label>
                  <select 
                    id="companyId"
                    value={manualForm.companyId} 
                    onChange={e => updateManualPrimaryCompany(e.target.value)} 
                    disabled={isScopedUserManager}
                    className={`bg-slate-950 border ${errors.companyId ? 'border-rose-500' : 'border-slate-800'} p-3 rounded-lg text-white outline-none disabled:opacity-70`}
                  >
                    <option value="">Selecione...</option>
                    {editableCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {errors.companyId && <span className="text-rose-500 text-[10px] font-bold uppercase">{errors.companyId}</span>}
                </div>

                {/* Cargo - 4 cols */}
                <div className="md:col-span-4 flex flex-col gap-1.5">
                  <label htmlFor="cargo" className="text-xs font-bold text-slate-500 uppercase tracking-widest">Cargo</label>
                  <select
                    id="cargo"
                    value={manualForm.cargo}
                    onChange={e => setManualForm(prev => ({ ...prev, cargo: e.target.value }))}
                    disabled={companyCargos.length === 0}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none disabled:opacity-70"
                  >
                    <option value="">{companyCargos.length === 0 ? 'Sem cargos cadastrados' : 'Nenhum'}</option>
                    {companyCargos.map(item => <option key={item.id} value={item.name}>{item.name}</option>)}
                  </select>
                </div>

                {/* Área - 4 cols */}
                <div className="md:col-span-4 flex flex-col gap-1.5">
                  <label htmlFor="area" className="text-xs font-bold text-slate-500 uppercase tracking-widest">Área</label>
                  <select
                    id="area"
                    value={manualForm.area}
                    onChange={e => setManualForm(prev => ({ ...prev, area: e.target.value }))}
                    disabled={companyAreas.length === 0}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none disabled:opacity-70"
                  >
                    <option value="">{companyAreas.length === 0 ? 'Sem áreas cadastradas' : 'Nenhuma'}</option>
                    {companyAreas.map(item => <option key={item.id} value={item.name}>{item.name}</option>)}
                  </select>
                </div>

                {manualForm.profile === 'Ponto Focal' && (
                  <div className="md:col-span-8 flex flex-col gap-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Empresas Vinculadas</label>
                    <MultiSelect
                      options={editableCompanies}
                      value={manualForm.companyIds}
                      onChange={updateManualLinkedCompanies}
                      placeholder="Selecionar empresas vinculadas"
                      disabled={isScopedUserManager}
                    />
                    <span className="text-[10px] text-slate-500">
                      A empresa principal tambem faz parte da lista e define o vinculo padrao do cadastro.
                    </span>
                    {errors.companyIds && <span className="text-rose-500 text-[10px] font-bold uppercase">{errors.companyIds}</span>}
                  </div>
                )}

                {/* CPF - 4 cols */}
                <div className="md:col-span-4 flex flex-col gap-1.5">
                  <label htmlFor="cpf" className="text-xs font-bold text-slate-500 uppercase tracking-widest">CPF (Opcional)</label>
                  <input 
                    id="cpf"
                    type="text" 
                    value={manualForm.cpf} 
                    onChange={e => setManualForm({...manualForm, cpf: e.target.value})} 
                    placeholder="000.000.000-00"
                    className={`bg-slate-950 border ${errors.cpf ? 'border-rose-500' : 'border-slate-800'} p-3 rounded-lg text-white focus:border-white outline-none transition`} 
                  />
                  {errors.cpf && <span className="text-rose-500 text-[10px] font-bold uppercase">{errors.cpf}</span>}
                </div>

                {/* WhatsApp - 4 cols (Opcional) */}
                <div className="md:col-span-4 flex flex-col gap-1.5">
                  <label htmlFor="whatsapp" className="text-xs font-bold text-slate-500 uppercase tracking-widest">WhatsApp (Opcional)</label>
                  <input 
                    id="whatsapp"
                    type="text" 
                    value={manualForm.whatsapp} 
                    onChange={e => setManualForm({...manualForm, whatsapp: e.target.value})} 
                    placeholder="(00) 00000-0000"
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white focus:border-white outline-none transition" 
                  />
                </div>

                {/* Parceiro SAS - 4 cols */}
                <div className="md:col-span-4 flex flex-col gap-1.5">
                  <label htmlFor="partnerId" className="text-xs font-bold text-slate-500 uppercase tracking-widest">Parceiro SAS</label>
                  <select 
                    id="partnerId"
                    value={manualForm.partnerId} 
                    onChange={e => setManualForm({...manualForm, partnerId: e.target.value})} 
                    disabled={isScopedUserManager}
                    className={`bg-slate-950 border ${errors.partnerId ? 'border-rose-500' : 'border-slate-800'} p-3 rounded-lg text-white outline-none disabled:opacity-70`}
                  >
                    <option value="">Selecione...</option>
                    {(isScopedUserManager ? editablePartnerOptions : (partners || [])).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  {errors.partnerId && <span className="text-rose-500 text-[10px] font-bold uppercase">{errors.partnerId}</span>}
                </div>

                {/* Status - 6 cols */}
                <div className="md:col-span-6 flex flex-col gap-1.5">
                  <label htmlFor="status" className="text-xs font-bold text-slate-500 uppercase tracking-widest">Status</label>
                  <select 
                    id="status"
                    value={manualForm.status} 
                    onChange={e => setManualForm({ ...manualForm, status: e.target.value })} 
                    disabled={isScopedUserManager}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none disabled:opacity-70"
                  >
                    <option value="Programado">Programado</option>
                    <option value="Pendente">Pendente</option>
                    <option value="Ativo">Ativo</option>
                    <option value="Inativo">Inativo</option>
                  </select>
                </div>

                {/* Perfil - 6 cols */}
                <div className="md:col-span-6 flex flex-col gap-1.5">
                  <label htmlFor="profile" className="text-xs font-bold text-slate-500 uppercase tracking-widest">Perfil</label>
                  <select 
                    id="profile"
                    value={manualForm.profile} 
                    onChange={e => updateManualProfile(e.target.value)} 
                    disabled={isScopedUserManager}
                    className="bg-slate-950 border border-slate-800 p-3 rounded-lg text-white outline-none disabled:opacity-70"
                  >
                    {rolesList.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                {(!editingId || canEditScheduledInvite) && (
                  <div className="md:col-span-12 rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                    <div className="flex flex-col gap-3">
                      <div>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Início do Convite</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {!editingId
                            ? 'Escolha quando o sistema deve disparar o convite deste usuário.'
                            : 'Ajuste a data e hora já programadas para o disparo do convite deste usuário.'}
                        </p>
                      </div>

                      {!editingId && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setManualForm((prev) => ({
                              ...prev,
                              inviteStartMode: INVITE_START_MODES.immediate,
                              inviteStartDate: '',
                              status: prev.status === 'Programado' ? 'Pendente' : prev.status,
                            }))}
                            className={`rounded-lg border px-4 py-3 text-left transition ${
                              manualForm.inviteStartMode === INVITE_START_MODES.immediate
                                ? 'border-orange-500 bg-orange-500/10 text-white'
                                : 'border-slate-800 bg-slate-950 text-slate-300 hover:border-slate-700'
                            }`}
                          >
                            <span className="block text-sm font-bold">Início Imediato</span>
                            <span className="block text-xs text-slate-400 mt-1">Mantém o fluxo atual e envia o convite assim que o cadastro for concluído.</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setManualForm((prev) => ({
                              ...prev,
                              inviteStartMode: INVITE_START_MODES.scheduled,
                              status: 'Programado',
                            }))}
                            className={`rounded-lg border px-4 py-3 text-left transition ${
                              manualForm.inviteStartMode === INVITE_START_MODES.scheduled
                                ? 'border-orange-500 bg-orange-500/10 text-white'
                                : 'border-slate-800 bg-slate-950 text-slate-300 hover:border-slate-700'
                            }`}
                          >
                            <span className="block text-sm font-bold">Programar Início</span>
                            <span className="block text-xs text-slate-400 mt-1">Cria o usuário agora e agenda o disparo do convite para a data escolhida.</span>
                          </button>
                        </div>
                      )}

                      {errors.inviteStartMode && (
                        <span className="text-rose-500 text-[10px] font-bold uppercase">{errors.inviteStartMode}</span>
                      )}

                      {manualForm.inviteStartMode === INVITE_START_MODES.scheduled && (
                        <div className="max-w-sm flex flex-col gap-1.5">
                          <ScheduledDateTimePicker
                            id="inviteStartDate"
                            label="Data e Hora de Início"
                            value={manualForm.inviteStartDate}
                            onChange={(nextValue) => setManualForm((prev) => ({ ...prev, inviteStartDate: nextValue }))}
                            error={errors.inviteStartDate}
                            helperText={`Formato: ${INVITE_START_PLACEHOLDER}. O convite sera disparado automaticamente na data e hora informadas no horario de Sao Paulo.`}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Matrícula Automática - Only for new users */}
              {!editingId && (
                <div className="bg-slate-950 border border-slate-800 rounded-lg p-5 mt-2">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-6 rounded-full p-1 cursor-pointer transition-colors ${autoEnroll ? 'bg-orange-500' : 'bg-slate-700'}`} onClick={() => setAutoEnroll(!autoEnroll)}>
                        <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${autoEnroll ? 'translate-x-4' : ''}`} />
                      </div>
                      <div>
                        <h4 className="font-bold text-white">Matrícula Automática</h4>
                        <p className="text-xs text-slate-500">Matricular usuário automaticamente em cursos/turmas</p>
                      </div>
                    </div>
                  </div>

                  {autoEnroll && (
                    <div className="flex flex-col gap-4 pl-2 border-l-2 border-slate-800 ml-4 mt-2 animate-fade-in">
                      {loadingResources ? (
                        <div className="py-4 text-center text-slate-500 flex items-center justify-center gap-2">
                          <Loader2 size={16} className="animate-spin" /> Carregando cursos e turmas...
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.8fr)_minmax(280px,1fr)] gap-4">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center justify-between gap-3">
                              <label className="text-xs font-bold text-slate-500 uppercase">Cursos</label>
                              <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                                {selectedCourses.length} selecionado(s)
                              </span>
                            </div>
                          <input
                            type="text"
                            value={courseSearch}
                            onChange={(e) => setCourseSearch(e.target.value)}
                            placeholder="Buscar curso..."
                            className="bg-slate-900 border border-slate-700 p-2 rounded-lg text-white text-sm focus:border-orange-500 outline-none placeholder:text-slate-500"
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={selectAllVisibleCourses}
                              disabled={filteredCourses.length === 0}
                              className="px-3 py-1.5 rounded-lg border border-slate-700 text-xs font-bold text-slate-300 hover:border-slate-500 hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Selecionar todos
                            </button>
                            <button
                              type="button"
                              onClick={clearSelectedCourses}
                              disabled={selectedCourses.length === 0}
                              className="px-3 py-1.5 rounded-lg border border-slate-700 text-xs font-bold text-slate-300 hover:border-slate-500 hover:text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Limpar seleção
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowOnlySelectedCourses((prev) => !prev)}
                              disabled={selectedCourses.length === 0}
                              className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition disabled:opacity-50 disabled:cursor-not-allowed ${
                                showOnlySelectedCourses
                                  ? 'border-orange-500 bg-orange-500/10 text-orange-300'
                                  : 'border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white'
                              }`}
                            >
                              Mostrar apenas selecionados
                            </button>
                          </div>
                            <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 max-h-72 overflow-y-auto space-y-2">
                              {availableCourses.length === 0 ? (
                                <p className="text-sm text-slate-500">Nenhum curso ativo disponível.</p>
                              ) : filteredCourses.length === 0 ? (
                                <p className="text-sm text-slate-500">Nenhum curso encontrado para a busca informada.</p>
                              ) : (
                                filteredCourses.map((course) => {
                                  const isSelected = selectedCourses.includes(course.id);
                                  return (
                                    <label
                                      key={course.id}
                                      className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${
                                        isSelected
                                          ? 'border-orange-500 bg-orange-500/10'
                                          : 'border-slate-700 bg-slate-950/60 hover:border-slate-500'
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-950 text-orange-500 focus:ring-orange-500"
                                        checked={isSelected}
                                        onChange={() => toggleCourseSelection(course.id)}
                                      />
                                      <span className="text-sm leading-5 text-white break-words">{course.name}</span>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                            <p className="text-[10px] text-slate-500">Marque todos os cursos em que o usuário deve ser matriculado.</p>
                          </div>

                          <div className="flex flex-col gap-4">
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-bold text-slate-500 uppercase">Validade (Dias)</label>
                              <input 
                                type="number" 
                                min="1"
                                value={validityDays}
                                onChange={(e) => setValidityDays(e.target.value)}
                                className="bg-slate-900 border border-slate-700 p-2 rounded-lg text-white focus:border-orange-500 outline-none"
                              />
                            </div>

                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-bold text-slate-500 uppercase">Vincular Turma (Opcional)</label>
                              <select 
                                value={selectedClass}
                                onChange={(e) => setSelectedClass(e.target.value)}
                                className="bg-slate-900 border border-slate-700 p-2 rounded-lg text-white focus:border-orange-500 outline-none"
                              >
                                <option value="">Nenhuma</option>
                                {availableClasses.map(c => (
                                  <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 mt-4 border-t border-slate-800 pt-6">
                <button 
                  type="button" 
                  onClick={closeManualModal} 
                  className="flex-1 px-5 py-3 rounded-lg font-bold text-slate-400 hover:bg-white/5 transition"
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  disabled={submitting || (autoEnroll && selectedCourses.length === 0)}
                  style={{ backgroundColor: partner.color }} 
                  className="flex-1 px-5 py-3 rounded-lg font-bold text-white shadow-lg hover:opacity-90 transition disabled:opacity-60"
                >
                  {submitting ? 'Salvando...' : (editingId ? 'Salvar Usuário' : 'Salvar e Convidar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
